import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";

import {
    useFormInput,
    comma,
    uncomma,
    dateFormatDateTime,
    dateFormatToISOString,
    onlyNumericInput,
    formatNumberWithCommas,
    formatDecimalInput,
    calculateRentalDuration,
    calculateRentPrice,
} from "@/utils";
import { Loading, Toast, Confirm } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";

import {
    useGetCarGubunQuery,
    useGetCarManufacturerQuery,
    useGetCarModelQuery,
    useGetCarModelDetailQuery,
    useGetCarClassQuery,
    useGetCarTrimQuery,
    useGetFuelListQuery,
    useGetRentPriceTableQuery,
    useLazyGetRentPriceTableDetailQuery,
    useGetRentPriceTableItemsQuery,
    useLazyGetRentPriceTableItemsQuery,
} from "@/redux/metaApi";
import {
    useGetAllCarsQuery,
    usePostCarMutation,
    usePutCarMutation,
    useLazySearchCarQuery,
    useLazySearchCarByNumberQuery,
    useGetCarByIdQuery,
    useDeleteCarMutation,
} from "@/redux/carApi";
import { useGetMyRentCorpQuery, useGetBranchInfoQuery } from "@/redux/rentCorpApi";
import { useGetInsuranceCorpQuery } from "@/redux/insuranceCorpApi";
import { useGetCarLocationListQuery } from "@/redux/carLocationApi";
import { useGetParkingLotListQuery, usePostParkingLotMutation } from "@/redux/parkingLotApi";
import { useGetOrderDetailMutation, usePutModifyOrderMutation, useGetOrderChatMutation, usePostOrderChatMutation } from "@/redux/orderApi";
import { useGetChargeListQuery, usePostChargeMutation, usePostPreChargeMutation, useGetChargeDetailQuery } from "@/redux/chargeApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import {
    useSignContractPdfMutation,
    useSendContractMutation,
    useReturnContractMutation,
    useUploadOriginContractPdfMutation,
    useGetContractGroupListQuery,
    usePostContractGroupMutation,
    usePutContractGroupMutation,
    useGetContractGroupByIdQuery,
    useDeleteContractGroupMutation,
} from "@/redux/contractApi";
import {
    useGetAgentePartnerQuery,
    usePutAgentePartnerMutation,
    useDeleteAgentePartnerMutation,
    useGetAgentePartnerListQuery,
    usePostAgentePartnerMutation,
    useGetMechanicPartnerQuery,
    usePutMechanicPartnerMutation,
    useDeleteMechanicPartnerMutation,
    useGetMechanicPartnerListQuery,
    usePostMechanicPartnerMutation,
    useGetSalesPartnerQuery,
    usePutSalesPartnerMutation,
    useDeleteSalesPartnerMutation,
    useGetSalesPartnerListQuery,
    usePostSalesPartnerMutation,
} from "@/redux/partnerApi";

import {
    originOptions,
    transmissionOptions,
    fuelRateOptions,
    vehicleStatusOptions,
    insuranceAgeOptions,
    contractMonthOptions,
    emergencyServiceOptions,
    daeinPay1Options,
    daeinPay2Options,
    daemulPayOptions,
    jasonOptions,
    selfCarDamageTypeOptions,
    selfCarDamageAmountOptions,
    selfCarDamageRateOptions,
    noInsInjuryOptions,
    capitalCorpOptions,
    driverTypeOptions,
    driverLicenseTypeOptions,
    orderTypeOptions,
    localCodeOptions,
    chargeRateOptions,
    chargeMethodOptions,
    displacementOptions,
} from "@/constants/selectOptions";

const INSURANCE_ITEM_CONFIGS = {
    DAEIN_PAY_1: {
        label: "대인 배상 I",
        coverageOptions: daeinPay1Options,
    },
    DAEIN_PAY_2: {
        label: "대인 배상 II",
        coverageOptions: daeinPay2Options,
    },
    DAEMUL_PAY: {
        label: "대물 배상",
        coverageOptions: daemulPayOptions,
    },
    JASON_DEAD: {
        label: "자동차상해(사망)",
        coverageOptions: jasonOptions,
    },
    JASON_INJURY: {
        label: "자동차상해(부상)",
        coverageOptions: jasonOptions,
    },
    JASON_BLOCK: {
        label: "자동차상해(장해)",
        coverageOptions: jasonOptions,
    },
    SELF_CAR_DAMAGE: {
        label: "자기차량손해",
        // 첫 번째 select: '금액'/'비율'
        // 두 번째 select는 첫 번째 선택값에 따라 selfCarDamageAmountOptions / selfCarDamageRateOptions
    },
    NO_INS_INJURY: {
        label: "무보험차상해",
        coverageOptions: noInsInjuryOptions,
    },
    EMERGENCY_SERVICE: {
        label: "긴급출동",
        coverageOptions: emergencyServiceOptions,
    },
};

// 초기 계약서 객체
const initialContract = {
    rentCorpId: null,
    carId: null,
    contractCar: {
        carNumber: "",
        cho: "",
        manufacturerName: "",
        modelName: "",
        modelDetailName: "",
        className: "",
        trimName: "",
        bmNo: "",
        boiNo: "",
        boNo: "",
        bsNo: "",
        bdNo: "",
        firstDate: "",
        color: "",
        fuel: "",
        motorType: "",
        displacement: 0,
        mileage: 0,
        gasCurrentAmount: 0,
    },
    insuranceItems: [
        {
            insuranceItemType: "DAEIN_PAY_1", // 대인배상1
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "DAEIN_PAY_2", // 대인배상2
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "DAEMUL_PAY", // 대물배상
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "NO_INS_INJURY", // 무보험차상해
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_DEAD", // 자동차상해(사망)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_INJURY", // 자동차상해(부상)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "JASON_BLOCK", // 자동차상해(장해)
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
        {
            insuranceItemType: "SELF_CAR_DAMAGE", // 자기차량손해
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
            selfCarDamageRangeType: null, // "PRICE" or "RATE" 선택값
            selfCarDamageRateType: null,
        },
        {
            insuranceItemType: "EMERGENCY_SERVICE", // 긴급출동
            joinYn: "N",
            limitType: null,
            compensationRange: null,
            selfPay: null,
        },
    ],
};

export const BillingRequest = () => {
    const { contractGroupId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const passedCarNumber = location.state?.carNumber;
    const passedOrderId = location.state?.orderId;

    const { formData, handleChange, setFormData } = useFormInput({
        // 공통
        mode: "CREATE", // "CREATE" / "READ" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        servicePopupStatus: false,
        addressPopupStatus: false,
        addressPopupTarget: null, // "MAIN" | "ADDITIONAL" | "ALLOCATION_RELEASE" | "ALLOCATION_RETURN" | "BUSINESS"
        agentPartnersPopupStatus: false,
        salesPartnersPopupStatus: false,
        vehicleListPopupStatus: false,
        vehicleReturnPopupStatus: false,
        vehicleReturnContractIdx: null,
        billingSettingPopupStatus: false,
        id: null, // contractGroupId
        additionalDriverStatus: false,
        businessStatus: false,
        contractType: "",
        memoForClient: "",
        memoForContract: "",
        memoForInsCorp: "",
        contractUploads: [
            {
                uploadType: "DRIVER_LICENSE_MAIN",
                uploadId: null,
            },
            {
                uploadType: "ADD_FILE",
                uploadId: null,
            },
        ],
        contractTerms: [],
        driverLicenseFileName: "",
        driverLicense2FileName: "",
        bizLicenseFileName: "",
        etcFileName: "",
        addFileName: "",
        addTermFileName: "",
        contractDrivers: [
            {
                contractDriverType: "MAIN",
                name: "",
                idNumber: "",
                phone: "",
                address: "",
                addressDetail: "",
                driverLicenseType: "",
                driverLicenseNumber: "",
                serialNumber: "",
                issueDate: "",
            },
        ],
        accidentCar: {
            carNumber: "",
            cho: "",
            manufacturerName: "",
            modelName: "",
            modelDetailName: "",
            className: "",
            trimName: "",
            bmNo: "",
            boiNo: "",
            boNo: "",
            bsNo: "",
            bdNo: "",
            firstDate: "",
            color: "",
            fuel: "",
            motorType: "",
            displacement: 0,
            mileage: 0,
        },
        contractInsurance: {
            insuranceCorpName: "",
            receiptNumber: "",
            managerName: "",
            managerPhone: "",
            faxNumber: "",
            email: "",
        },
        contractMechanic: {
            mechanicCorpName: "",
            phone: "",
        },
        contracts: [{ ...initialContract }],
    });
    const {
        mode,
        toastMsg,
        confirmStatus,
        servicePopupStatus,
        addressPopupStatus,
        agentPartnersPopupStatus,
        salesPartnersPopupStatus,
        vehicleListPopupStatus,
        vehicleReturnPopupStatus,
        billingSettingPopupStatus,
        id,
    } = formData;
    const [selectedContractIndex, setSelectedContractIndex] = useState(0); // 선택한 계약서 인덱스

    const [postContractGroup, { data: postData, isLoading: isPostLoading, error: isPostError }] = usePostContractGroupMutation();
    const [putContractGroup, { data: putData, isLoading: isPutLoading, error: isPutError }] = usePutContractGroupMutation();
    const [deleteContractGroup, { isLoading: isDeleteLoading }] = useDeleteContractGroupMutation();
    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();
    const [doSearchCar, { data: searchCarData, isSearchLoading, isSearchError }] = useLazySearchCarByNumberQuery();
    const [doCharge, { data: chargeData, isChargeLoading, isChargeError }] = usePostChargeMutation();
    const [doChargePre, { data: chargePreData, isChargePreLoading, isChargePreError }] = usePostPreChargeMutation();
    const [getOrderDetail, { data: detailData, isLoading: detailIsLoading, error: detailError }] = useGetOrderDetailMutation();
    const {
        data: contractGroupData,
        isLoading: isContractGroupLoading,
        isFetching: isContractGroupFetching,
        error: contractGroupError,
    } = useGetContractGroupByIdQuery(contractGroupId, {
        skip: !contractGroupId,
        refetchOnMountOrArgChange: true,
    });

    // ─────────────────────────────────────────────────────────────────────────
    // 1) 모드 설정 (UPDATE / CREATE)
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (contractGroupId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: contractGroupId,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                mode: "CREATE",
            }));
        }
    }, [contractGroupId, setFormData]);

    // ─────────────────────────────────────────────────────────────────────────
    // 2) UPDATE 모드일 때: 계약 상세조회 (contractDetailData) 매핑
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        // CREATE 모드일 때 early return
        if (!contractGroupId) return;

        if (contractGroupData && contractGroupData.data) {
            const response = contractGroupData.data;
            // contractDrivers 변환: idNumber 분리 및 driverLicenseNumber 처리
            const mappedContractDrivers = (response.contractDrivers || []).map((driver) => {
                let updatedDriver = { ...driver };
                // idNumber 분리: 앞 6자리와 7번째 자리
                if (updatedDriver.idNumber && updatedDriver.idNumber.length >= 7) {
                    updatedDriver.idNumberF = updatedDriver.idNumber.slice(0, 6);
                    updatedDriver.idNumberB = updatedDriver.idNumber.slice(6, 7);
                }
                // driverLicenseNumber 분리: 앞 2자리를 Number 타입의 localCode로 저장하고, 나머지 문자열 재설정
                if (updatedDriver.driverLicenseNumber && updatedDriver.driverLicenseNumber.length >= 2) {
                    updatedDriver.localCode = Number(updatedDriver.driverLicenseNumber.slice(0, 2));
                    updatedDriver.driverLicenseNumber = updatedDriver.driverLicenseNumber.slice(2);
                }
                return updatedDriver;
            });
            // 제2 운전자가 있으면 additionalDriverStatus를 true로 설정
            const additionalDriverExists = mappedContractDrivers.some((driver) => driver.contractDriverType === "ADDITIONAL");
            // contractBusinessInfo가 존재하면 businessStatus를 true로 설정
            const businessStatus = response.contractBusinessInfo ? true : false;

            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                ...response,
                // contractDrivers 변환 결과 반영
                contractDrivers: mappedContractDrivers,
                additionalDriverStatus: additionalDriverExists,
                businessStatus: businessStatus,
                // 필요시 contractCar 등 다른 필드도 추가 매핑
            }));
        }
    }, [contractGroupId, contractGroupData, setFormData]);

    // ─────────────────────────────────────────────────────────────────────────
    // 3) CREATE 모드일 때: passedCarNumber 또는 passedOrderId 처리
    // ─────────────────────────────────────────────────────────────────────────
    // 3-1) passedCarNumber가 있으면 차량 조회
    useEffect(() => {
        if (!contractGroupId && passedCarNumber) {
            // passedCarNumber가 존재하면 handleSearchCar() 로직 실행
            // (handleSearchCar 내부에서 doSearchCar 호출 → formData에 세팅)
            handleSearchCar(passedCarNumber);
        }
    }, [contractGroupId, passedCarNumber]);

    // 3-2) passedOrderId가 있으면 오더 상세조회 → formData에 세팅
    useEffect(() => {
        if (!contractGroupId && passedOrderId) {
            // passedOrderId가 존재하면 getOrderDetail API 호출
            getOrderDetail(passedOrderId)
                .unwrap()
                .then((res) => {
                    const response = res.data;
                    console.log(response);

                    setFormData((prev) => ({
                        ...prev,
                        isLoading: false,
                        // ...response,
                        orderId: passedOrderId,
                        contractType: response?.orderType,
                        accidentCar: {
                            bdNo: response?.bdNo,
                            bmNo: response?.bmNo,
                            boNo: response?.boNo,
                            boiNo: response?.boiNo,
                            bsNo: response?.bsNo,
                            carNumber: response?.carNumber,
                            cho: response?.cho,
                            className: response?.className,
                            displacement: comma(response?.displacement),
                            manufacturerName: response?.manufacturerName,
                            modelDetailName: response?.modelDetailName,
                            modelName: response?.modelName,
                            trimName: response?.trimName,
                        },
                        customerName: response?.orderCustomer?.name,
                        customerPhone: response?.orderCustomer?.phone,
                        totalOrderCharge: {
                            chargeMethod:
                                response?.orderInsurance?.chargeMethod === "RENT_PRICE"
                                    ? response?.orderInsurance?.rentPriceTableId
                                    : response?.orderInsurance?.chargeMethod,
                            chargeRate: response?.orderInsurance?.chargeRate,
                        },
                        contractInsurance: {
                            ...response?.contractInsurance,
                            id: response?.orderInsurance.id,
                            insuranceCorpName: response?.orderInsurance.insuranceCorpName,
                            receiptNumber: response?.orderInsurance.receiptNumber,
                            managerName: response?.orderInsurance.managerName,
                            managerPhone: response?.orderInsurance.managerPhone,
                            faxNumber: response?.orderInsurance.faxNumber,
                            email: response?.orderInsurance.email,
                        },
                        contractMechanic: {
                            ...response?.contractMechanic,
                            mechanicCorpName: response?.orderMechanic?.mechanicCorpName,
                            phone: response?.orderMechanic?.phone,
                        },
                        contracts: prev.contracts.map((ct, idx) => ({
                            ...ct,
                            contractCar: {
                                ...ct.contractCar,
                            },
                        })),
                    }));
                })
                .catch((err) => {
                    console.error("오더 상세 조회 에러:", err);
                });
        }
    }, [contractGroupId, passedOrderId, getOrderDetail, setFormData]);

    const handleSelectCar = () => {
        setFormData((prev) => ({ ...prev, vehicleListPopupStatus: true }));
    };

    const handleSearchCar = async (carNumber, contractIndex = 0) => {
        // 1. 유효성 검사
        if (!carNumber) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량번호를 입력해주세요.",
            }));
            return;
        }

        // 2. 로딩 상태 활성화
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            // 3. API 호출
            const res = await doSearchCar({ car_number: carNumber || formData.contracts[contractIndex].contractCar.carNumber });
            const carData = res?.data?.data;
            const carId = carData.id;

            if (carData) {
                setFormData((prev) => {
                    const updatedContracts = [...prev.contracts];
                    updatedContracts[contractIndex] = {
                        ...updatedContracts[contractIndex],
                        contractCar: carData,
                        carId: carId,
                    };
                    return {
                        ...prev,
                        isLoading: false,
                        toastMsg: "차량 조회 완료",
                        contracts: updatedContracts,
                    };
                });
            } else {
                throw new Error("차량 조회 실패");
            }
        } catch (error) {
            console.error("차량 조회 에러:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량 조회 실패",
                isLoading: false,
            }));
        }
    };

    // 전달받은 차량번호가 있으면 자동으로 formData에 세팅하고 handleSearchCar 호출
    useEffect(() => {
        if (passedCarNumber) {
            setFormData((prev) => ({
                ...prev,
                contractCar: { ...prev.contractCar, carNumber: passedCarNumber },
            }));
            handleSearchCar(passedCarNumber);
        }
    }, [passedCarNumber]);

    useEffect(() => {
        setFormData((prev) => {
            let drivers = [...prev.contractDrivers];
            if (prev.additionalDriverStatus) {
                // 추가 운전자가 없으면 추가
                if (!drivers.some((d) => d.contractDriverType === "ADDITIONAL")) {
                    drivers.push({
                        contractDriverType: "ADDITIONAL",
                        name: "",
                        idNumber: "",
                        phone: "",
                        address: "",
                        addressDetail: "",
                        bCode: "",
                        driverLicenseType: "",
                        driverLicenseNumber: "",
                        serialNumber: "",
                        issueDate: "",
                    });
                }
            } else {
                // 추가 운전자 제거
                drivers = drivers.filter((d) => d.contractDriverType !== "ADDITIONAL");
            }
            return { ...prev, contractDrivers: drivers };
        });
    }, [formData.additionalDriverStatus, setFormData]);

    // buildRequestBody – 그룹 레벨 필드와 contracts 배열 포함
    const buildRequestBody = () => {
        let reqBody = { ...formData };

        // UI용 상태 값들 제거
        delete reqBody.mode;
        delete reqBody.toastMsg;
        delete reqBody.confirmStatus;
        delete reqBody.servicePopupStatus;
        delete reqBody.id;
        delete reqBody.additionalDriverStatus;
        delete reqBody.businessStatus;
        delete reqBody.driverLicenseFileName;
        delete reqBody.driverLicense2FileName;
        delete reqBody.bizLicenseFileName;
        delete reqBody.etcFileName;
        delete reqBody.addFileName;
        delete reqBody.addTermFileName;
        delete reqBody.vehicleReturnPopupStatus;
        delete reqBody.billingSettingPopupStatus;
        delete reqBody.addressPopupStatus;
        delete reqBody.addressPopupTarget;
        delete reqBody.agentPartnersPopupStatus;
        delete reqBody.salesPartnersPopupStatus;
        delete reqBody.vehicleListPopupStatus;

        // accidentCar.displacement가 문자열로 콤마 포함 시 uncomma 처리
        if (reqBody.accidentCar && typeof reqBody.accidentCar.displacement === "string") {
            reqBody.accidentCar.displacement = Number(uncomma(reqBody.accidentCar.displacement));
        }

        // contracts 배열 내 각 계약서의 contractCar 필드 처리
        if (Array.isArray(reqBody.contracts)) {
            reqBody.contracts = reqBody.contracts.map((contract) => {
                if (contract?.contractCar) {
                    if (typeof contract?.contractCar.displacement === "string") {
                        contract.contractCar.displacement = Number(uncomma(contract?.contractCar.displacement));
                    }
                    if (typeof contract?.contractCar.mileage === "string") {
                        contract.contractCar.mileage = Number(uncomma(contract?.contractCar.mileage));
                    }
                    // 필요 시 다른 숫자 필드도 동일하게 처리
                }
                return contract;
            });
        }

        if (Array.isArray(reqBody.contractDrivers)) {
            reqBody.contractDrivers = reqBody.contractDrivers.map((driver) => {
                // 주민등록번호 idNumberF + idNumberB
                if (driver.idNumberF !== undefined && driver.idNumberB !== undefined) {
                    driver.idNumber = driver.idNumberF + driver.idNumberB;
                    delete driver.idNumberF;
                    delete driver.idNumberB;
                }
                // 면허번호 localCode + driverLicenseNumber 재설정
                if (driver.localCode && driver.driverLicenseNumber) {
                    driver.driverLicenseNumber = String(driver.localCode) + driver.driverLicenseNumber;
                    delete driver.localCode;
                }
                return driver;
            });
        }

        return reqBody;
    };

    const handleSubmit = async (action) => {
        setFormData((prev) => ({
            ...prev,
            isLoading: true,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = buildRequestBody();
            await action(requestBody);

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/billing/status"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
                isLoading: false,
            }));
        } finally {
        }
    };

    // 차량 등록 시 호출 (CREATE)
    const handleSaveConfirm = async () => {
        await handleSubmit((requestBody) => postContractGroup(requestBody).unwrap());
    };

    // 차량 수정 시 호출 (UPDATE)
    const handleUpdateConfirm = async () => {
        await handleSubmit((requestBody) => putContractGroup({ contractGroupId: formData.id, body: requestBody }).unwrap());
    };

    const handleSave = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `계약서 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: mode === "CREATE" ? handleSaveConfirm : handleUpdateConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    useEffect(() => {
        if (postData) {
            navigate(`/contract/edit/${postData?.data.id}`);
        }
    }, [postData]);

    useEffect(() => {
        if (putData) {
            window.location.reload();
        }
    }, [putData]);

    const showConfirm = (title, description, buttons) => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: title,
                confirmDescription: description,
                confirmButtons: buttons,
            },
        }));
    };

    // 삭제 확인 핸들러 (API 호출)
    const handleDeleteConfirm = async () => {
        try {
            await deleteContractGroup({
                contractGroupId: formData.id,
            }).unwrap();
            showConfirm("삭제가 완료되었습니다.", "", [
                {
                    label: "확인",
                    className: "btn-primary",
                    onClick: () => navigate("/contract/status"),
                },
            ]);
        } catch (error) {
            console.error("Failed to delete contract:", error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "계약서 정보 삭제에 실패했습니다.",
            }));
        }
    };

    // 삭제 버튼 클릭 시 확인 모달을 띄우고, "예" 버튼 클릭 시 handleDeleteConfirm 실행
    const handleDelete = () => {
        showConfirm("해당 계약서 정보를 삭제하시겠습니까?", "삭제 후에는 복구할 수 없습니다.", [
            { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
            {
                label: "아니오",
                className: "btn-cancel",
                onClick: () =>
                    setFormData((prev) => ({
                        ...prev,
                        confirmStatus: { confirmPopupStatus: false },
                    })),
            },
        ]);
    };

    // 공통 파일 업로드 핸들러
    const handleCommonFileChange = async (e, uploadType, idx) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;
        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);
            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;
            setFormData((prev) => {
                // 만약 uploadType이 "ADD_TERM"이면, contractTerms[idx] 업데이트
                if (uploadType === "ADD_TERM") {
                    const updatedTerms = [...(prev.contractTerms || [])];
                    updatedTerms[idx] = {
                        ...updatedTerms[idx],
                        upload: { id: newUploadId },
                        fileName: fileObj.name,
                    };
                    return { ...prev, contractTerms: updatedTerms };
                }
                // 그 외: contractUploads 배열 업데이트
                const updatedUploads = [...(prev.contractUploads || [])];
                const index = updatedUploads.findIndex((x) => x.uploadType === uploadType);
                if (index >= 0) {
                    updatedUploads[index] = {
                        ...updatedUploads[index],
                        upload: { id: newUploadId, fileName: fileObj.name },
                    };
                } else {
                    updatedUploads.push({ uploadType, upload: { id: newUploadId, fileName: fileObj.name } });
                }
                return { ...prev, contractUploads: updatedUploads };
            });
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "파일 업로드 실패",
            }));
        }
    };

    const getTermFileName = (idx) => {
        return formData.contractTerms?.[idx]?.fileName || "";
    };

    const getFileName = (uploadType) => {
        const uploadObj = formData.contractUploads?.find((x) => x.uploadType === uploadType);
        return uploadObj?.upload?.fileName || "";
    };

    // 그룹 내 계약서 추가/삭제 핸들러
    const addContract = () => {
        setFormData((prev) => ({
            ...prev,
            contracts: [...prev.contracts, { ...initialContract }],
        }));
    };

    const deleteContract = (index) => {
        setFormData((prev) => ({
            ...prev,
            contracts: prev.contracts.filter((_, idx) => idx !== index),
        }));
    };

    // 약관 추가: mandatoryYn은 무조건 "Y"로 세팅, id는 임시 값(Date.now()) 사용 (실제 id는 서버에서 할당)
    const handleAddTerm = () => {
        setFormData((prev) => ({
            ...prev,
            contractTerms: [...(prev.contractTerms || []), { id: null, upload: { id: null }, name: "", mandatoryYn: "Y" }],
        }));
    };

    // 청구하기
    const handleCharge = async () => {
        const payload = {
            contractGroup: {
                id: formData.id,
            },
            chargeItems: [
                {
                    contractId: 0,
                    rentPriceTableItemId: 0,
                    baseRentPrice: 0,
                    rentDays: 0,
                    rentHours: 0,
                    chargeRate: 0,
                    platformCommissionRate: 0,
                    orderCommissionRate: 0,
                    rentFee: 0,
                    insuranceChargeFee: 0,
                    platformCommissionFee: 0,
                    exceptCommissionFee: 0,
                    exceptAddTaxFee: 0,
                    orderCommissionFee: 0,
                    obtainEarningFee: 0,
                    allocation: {
                        id: 0,
                        releasedAt: "",
                        releaseMethod: "",
                        releaseAddress: "",
                        releaseAddressDetail: "",
                        returnedAt: "",
                        returnMethod: "",
                        returnAddress: "",
                        returnAddressDetail: "",
                        memo: "",
                    },
                },
            ],
        };

        try {
            await doCharge({
                contractGroupId: formData.id,
                contractId: formData.vehicleReturnContractIdx,
                body: payload,
            }).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "반납 등록이 완료되었습니다.",
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "반납 등록에 실패하였습니다.",
            }));
        }
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">{mode === "CREATE" ? "청구 상세" : "임대차 계약서 상세"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>
                                뒤로가기
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="order-detail-info" style={{ padding: "0" }}>
                                <div className="info-content" style={{ padding: "20px" }}>
                                    <div className="detail-summary">
                                        <div className="summary-info">
                                            <span className="insurance-type">
                                                보험대차
                                                {/* {orderTypeOptions.find((e) => e.value === orderType)?.label} */}
                                            </span>
                                            <span className="insurance-num">
                                                MG96362sdfg34
                                                {/* {orderInsurance.receiptNumber} */}
                                            </span>
                                        </div>
                                        <div className="order-status">
                                            매칭완료
                                            {/* {orderSubStatusOption.find((e) => e.value === orderSubStatus)?.label} */}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계약 유형</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ContractInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">
                                        운전자 정보 <i></i>
                                    </span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <DriverInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            getFileName={getFileName}
                                            handleCommonFileChange={handleCommonFileChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {formData.additionalDriverStatus && (
                                <div className="form-item">
                                    <div className="item-head">
                                        <span className="form-item_tit">제 2 운전자 정보</span>
                                        <div className="form-item_etc"></div>
                                    </div>
                                    <div className="item-body">
                                        <div className="item-list">
                                            <AdditionalDriverInfo
                                                formData={formData}
                                                handleChange={handleChange}
                                                setFormData={setFormData}
                                                getFileName={getFileName}
                                                handleCommonFileChange={handleCommonFileChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {formData.businessStatus && (
                                <div className="form-item">
                                    <div className="item-head">
                                        <span className="form-item_tit">사업자 정보</span>
                                        <div className="form-item_etc"></div>
                                    </div>
                                    <div className="item-body">
                                        <div className="item-list">
                                            <BusinessInfo
                                                formData={formData}
                                                handleChange={handleChange}
                                                setFormData={setFormData}
                                                getFileName={getFileName}
                                                handleCommonFileChange={handleCommonFileChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 그룹(계약서 그룹) 영역 – 계약 리스트 */}
                            <div className="form-group">
                                <div className="group-head">
                                    <div className="head-tit">계약 리스트</div>
                                    <div className="head-option">
                                        {/* <button type="button" className="btn btn-outline" onClick={addContract}>
                                            계약서 추가
                                        </button> */}
                                    </div>
                                </div>

                                <div className="group-content">
                                    {formData.contracts.map((contract, idx) => (
                                        <div key={idx} className="group-item">
                                            <div className="group-item-head">
                                                <div className="head-tit">계약-{idx + 1}</div>
                                                <div className="head-option">
                                                    {mode !== "CREATE" && (
                                                        <>
                                                            {idx >= 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-cancel"
                                                                    onClick={() => deleteContract(idx)}
                                                                >
                                                                    계약 지우기
                                                                </button>
                                                            )}
                                                            {mode !== "CREATE" && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-fourth"
                                                                    onClick={() => {
                                                                        window.open(
                                                                            `/contract/preview/${contractGroupId}/${contract?.id}`,
                                                                            "_blank"
                                                                        );
                                                                    }}
                                                                >
                                                                    계약서 보기
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary"
                                                                onClick={() => {
                                                                    setFormData((prevState) => ({
                                                                        ...prevState,
                                                                        vehicleReturnPopupStatus: true,
                                                                        vehicleReturnContractIdx: contract?.id,
                                                                    }));
                                                                }}
                                                            >
                                                                반납 등록
                                                            </button>
                                                            <button type="button" className="btn btn-cancel">
                                                                배차 취소
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="group-item-body">
                                                <div className="form-item">
                                                    <div className="item-head">
                                                        <span className="form-item_tit">대여차량 정보</span>
                                                        <div className="form-item_etc"></div>
                                                    </div>
                                                    <div className="item-body">
                                                        <div className="item-list">
                                                            <VehicleInfo
                                                                contract={contract}
                                                                onContractChange={(newValues) => {
                                                                    setFormData((prev) => {
                                                                        const updated = [...prev.contracts];
                                                                        updated[idx] = {
                                                                            ...updated[idx],
                                                                            ...newValues,
                                                                        };
                                                                        return { ...prev, contracts: updated };
                                                                    });
                                                                }}
                                                                handleSearchCar={() => {
                                                                    setSelectedContractIndex(idx);
                                                                    handleSelectCar();
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-item">
                                                    <div className="item-head">
                                                        <span className="form-item_tit">배차/반납 정보</span>
                                                        <div className="form-item_etc"></div>
                                                    </div>
                                                    <div className="item-body">
                                                        <div className="item-list">
                                                            <ReturnInfo
                                                                allocation={contract?.allocation}
                                                                onAllocationChange={(newValues) =>
                                                                    setFormData((prev) => {
                                                                        const updated = [...prev.contracts];
                                                                        updated[idx] = {
                                                                            ...updated[idx],
                                                                            allocation: { ...updated[idx].allocation, ...newValues },
                                                                        };
                                                                        return { ...prev, contracts: updated };
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="form-item">
                                                    <div className="item-head">
                                                        <span className="form-item_tit">보험 정보</span>
                                                        <div className="form-item_etc"></div>
                                                    </div>
                                                    <div className="item-body">
                                                        <div className="item-list">
                                                            <InsuranceTable
                                                                insuranceItems={contract?.insuranceItems}
                                                                onUpdateInsuranceItem={(index, newValues) =>
                                                                    setFormData((prev) => {
                                                                        const updated = [...prev.contracts];
                                                                        updated[idx] = {
                                                                            ...updated[idx],
                                                                            insuranceItems: updated[idx].insuranceItems?.map((item, i) =>
                                                                                i === index ? { ...item, ...newValues } : item
                                                                            ),
                                                                        };
                                                                        return { ...prev, contracts: updated };
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {mode !== "CREATE" && (
                                                    <div className="form-item">
                                                        <div className="item-head">
                                                            <span className="form-item_tit">청구 정보</span>
                                                        </div>
                                                        <div className="item-body">
                                                            <div className="item-list">
                                                                <BillingInfo
                                                                    formData={formData}
                                                                    setFormData={setFormData}
                                                                    contract={contract}
                                                                    onContractChange={(newValues) => {
                                                                        setFormData((prev) => {
                                                                            const updated = [...prev.contracts];
                                                                            updated[idx] = {
                                                                                ...updated[idx],
                                                                                billingInfo: { ...updated[idx].billingInfo, ...newValues },
                                                                            };
                                                                            return { ...prev, contracts: updated };
                                                                        });
                                                                    }}
                                                                    selectedContractIndex={selectedContractIndex}
                                                                    setSelectedContractIndex={setSelectedContractIndex}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="group-item-footer"></div>
                                        </div>
                                    ))}
                                </div>

                                {mode !== "CREATE" && (
                                    <>
                                        <div className="group-bottom">
                                            <div className="bottom-option">
                                                <button type="button" className="btn btn-sm btn-primary" onClick={addContract}>
                                                    계약 추가
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {(mode !== "CREATE" || passedOrderId) && (
                                <div className="form-item">
                                    <div className="item-head">
                                        <span className="form-item_tit">종합 청구 정보</span>
                                        <div className="form-item_etc"></div>
                                    </div>
                                    <div className="item-body">
                                        <div className="item-list">
                                            <TotalBillingInfo
                                                formData={formData}
                                                handleChange={handleChange}
                                                setFormData={setFormData}
                                                passedOrderId={passedOrderId}
                                            />
                                        </div>
                                    </div>
                                    {mode !== "CREATE" && (
                                        <>
                                            <div className="item-bottom">
                                                <div className="bottom-option">
                                                    <button type="button" className="btn btn-xs btn-primary" onClick={handleCharge}>
                                                        청구하기
                                                    </button>
                                                    <button type="button" className="btn btn-xs btn-secondary">
                                                        선청구 하기
                                                    </button>
                                                    <button type="button" className="btn btn-xs btn-cancel">
                                                        청구취소
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">사고차량 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        {/* AccidentInfo는 그룹 레벨의 accidentCar 사용 */}
                                        <AccidentInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">보험사 및 공업사 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <InsuranceInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기타 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <OtherInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">
                                        기타 특이사항 <i>고객 계약서에 기재됩니다.</i>
                                    </span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <OtherEtcInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">
                                        특이사항 <i>계약서 메모란에 공유됩니다.</i>
                                    </span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <EtcInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">
                                        보험사 전달사항 <i>청구시 보험사에 전달됩니다.</i>
                                    </span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <InsuranceDeliveredInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">약관 확인</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <TermsFile
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            getFileName={getFileName}
                                            getTermFileName={getTermFileName}
                                            handleCommonFileChange={handleCommonFileChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계약 관련 서류</span>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <AttachedFile
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            getFileName={getFileName}
                                            handleCommonFileChange={handleCommonFileChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                            addressPopupTarget: null,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => {
                            let updatedDrivers = [...prev.contractDrivers];
                            let updatedBusinessCorp = { ...prev.contractBusinessInfo };

                            switch (prev.addressPopupTarget) {
                                case "MAIN":
                                    updatedDrivers = prev.contractDrivers.map((driver) =>
                                        driver.contractDriverType === "MAIN"
                                            ? { ...driver, bCode: data.bcode, address: data.address }
                                            : driver
                                    );
                                    break;
                                case "ADDITIONAL":
                                    updatedDrivers = prev.contractDrivers.map((driver) =>
                                        driver.contractDriverType === "ADDITIONAL"
                                            ? { ...driver, bCode: data.bcode, address: data.address }
                                            : driver
                                    );
                                    break;
                                case "ALLOCATION_RELEASE":
                                case "ALLOCATION_RETURN": {
                                    // addressPopupTarget is expected to be like "ALLOCATION_RELEASE_{contractGroupId}" or "ALLOCATION_RETURN_{contractGroupId}"
                                    const [target, contractGroupId] = prev.addressPopupTarget.split("_").slice(-2);
                                    const updatedContracts = prev.contracts.map((contract) =>
                                        String(contract?.id) === contractGroupId
                                            ? {
                                                  ...contract,
                                                  allocation:
                                                      target === "ALLOCATION"
                                                          ? {
                                                                ...contract?.allocation,
                                                                releasebCode: data.bcode,
                                                                releaseAddress: data.address,
                                                            }
                                                          : {
                                                                ...contract?.allocation,
                                                                returnbCode: data.bcode,
                                                                returnAddress: data.address,
                                                            },
                                              }
                                            : contract
                                    );
                                    return {
                                        ...prev,
                                        contracts: updatedContracts,
                                        addressPopupStatus: false,
                                        addressPopupTarget: null,
                                    };
                                }
                                case "BUSINESS":
                                    updatedBusinessCorp = { ...prev.contractBusinessInfo, bCode: data.bcode, address: data.address };
                                    break;
                                default:
                                    console.warn("Unknown addressPopupTarget:", prev.addressPopupTarget);
                            }
                            return {
                                ...prev,
                                contractDrivers: updatedDrivers,
                                // contractBusinessInfo은 오직 "BUSINESS"일 때만 업데이트, 그 외에는 기존 값 유지
                                contractBusinessInfo:
                                    prev.addressPopupTarget === "BUSINESS" ? updatedBusinessCorp : prev.contractBusinessInfo,
                                addressPopupStatus: false,
                                addressPopupTarget: null,
                            };
                        })
                    }
                />
            )}

            {agentPartnersPopupStatus && (
                <AgentPartnersPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, agentPartnersPopupStatus: false }))}
                    formData={formData.productItemType}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    onAgentPartnerSelect={(partnerData) => {
                        // setFormData((prev) => {
                        //     const updated = [...prev.contracts];
                        //     updated[selectedContractIndex] = {
                        //         ...updated[selectedContractIndex],
                        //         contractCar: carData,
                        //         carId: carData.id,
                        //     };
                        //     return { ...prev, contracts: updated };
                        // });
                    }}
                />
            )}

            {salesPartnersPopupStatus && (
                <SalesPartnersPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, salesPartnersPopupStatus: false }))}
                    formData={formData.productItemType}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    onSalesPartnerSelect={(partnerData) => {
                        // setFormData((prev) => {
                        //     const updated = [...prev.contracts];
                        //     updated[selectedContractIndex] = {
                        //         ...updated[selectedContractIndex],
                        //         contractCar: carData,
                        //         carId: carData.id,
                        //     };
                        //     return { ...prev, contracts: updated };
                        // });
                    }}
                />
            )}

            {formData.vehicleListPopupStatus && (
                <VehicleListPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, vehicleListPopupStatus: false }))}
                    setFormData={setFormData}
                    // 전달하는 rentCorpId는 해당 계약서의 rentCorpId
                    rentCorpId={formData.contracts[selectedContractIndex].rentCorpId}
                    onRentCorpSelect={(value) => {
                        setFormData((prev) => {
                            const updated = [...prev.contracts];
                            updated[selectedContractIndex] = {
                                ...updated[selectedContractIndex],
                                rentCorpId: value,
                            };
                            return { ...prev, contracts: updated };
                        });
                    }}
                    onVehicleSelect={(vehicleData) => {
                        // doSearchCar API 호출 후 결과를 해당 계약서의 contractCar에 업데이트
                        doSearchCar({ car_number: vehicleData.carNumber })
                            .then((res) => {
                                const carData = res.data.data;
                                setFormData((prev) => {
                                    const updated = [...prev.contracts];
                                    updated[selectedContractIndex] = {
                                        ...updated[selectedContractIndex],
                                        contractCar: carData,
                                        carId: carData.id,
                                    };
                                    return { ...prev, contracts: updated };
                                });
                            })
                            .catch((error) => {
                                console.error("차량 조회 에러:", error);
                            });
                    }}
                />
            )}

            {vehicleReturnPopupStatus && (
                <VehicleReturnPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, vehicleReturnPopupStatus: false, vehicleReturnContractIdx: null }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                />
            )}

            {billingSettingPopupStatus && (
                <BillingSettingPopup
                    layer={true}
                    onClose={() => {
                        setFormData((prev) => ({
                            ...prev,
                            billingSettingPopupStatus: false,
                        }));
                        setSelectedContractIndex(null);
                    }}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                    selectedContractIndex={selectedContractIndex}
                />
            )}
        </>
    );
};

/* 계약 유형 */
const ContractInfo = ({ formData, handleChange, setFormData }) => {
    const { contractType } = formData;

    return (
        <>
            {/* 계약유형 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>계약유형</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={orderTypeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="계약유형 선택"
                            value={orderTypeOptions.find((opt) => opt.value === contractType)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    contractType: option.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>
        </>
    );
};

/* 계약차량 정보 */
const VehicleInfo = ({ contract, onContractChange, handleSearchCar }) => {
    const contractCar = contract?.contractCar;
    const { data: carGubunData } = useGetCarGubunQuery();
    const carGubunOptions =
        carGubunData && carGubunData.data
            ? carGubunData.data.map((item) => ({
                  value: item.bgNo,
                  label: item.bgName,
              }))
            : [];
    const { data: manufacturerData } = useGetCarManufacturerQuery({ bmNo: null });
    const manufacturerOptions =
        manufacturerData?.data?.map((item) => ({
            value: item.bmNo,
            label: item.bmName,
        })) || [];
    const { data: modelData } = useGetCarModelQuery({ bmNo: contractCar?.bmNo }, { skip: !contractCar?.bmNo });
    const modelOptions =
        modelData?.data?.map((item) => ({
            value: item.boiNo,
            label: item.boiName,
        })) || [];
    const { data: detailModelData } = useGetCarModelDetailQuery({ boiNo: contractCar?.boiNo }, { skip: !contractCar?.boiNo });
    const detailModelOptions =
        detailModelData?.data?.map((item) => ({
            value: item.boNo,
            label: item.boName,
        })) || [];
    const { data: carClassData } = useGetCarClassQuery({ boNo: contractCar?.boiNo }, { skip: !contractCar?.boiNo });
    const carClassOptions =
        carClassData?.data?.map((item) => ({
            value: item.bsNo,
            label: item.bsName,
        })) || [];
    const { data: carTrimData } = useGetCarTrimQuery({ bsNo: contractCar?.bsNo }, { skip: !contractCar?.bsNo });
    const carTrimOptions =
        carTrimData?.data?.map((item) => ({
            value: item.bdNo,
            label: item.bdName,
        })) || [];
    const { data: fuelData } = useGetFuelListQuery();
    const fuelOptions =
        fuelData && fuelData.data
            ? fuelData.data.map((item) => ({
                  value: item.bfNo,
                  label: item.bfName,
              }))
            : [];

    return (
        <>
            {/* 차량번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>차량번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="carNumber"
                            placeholder=""
                            value={contractCar?.carNumber || ""}
                            onChange={(e) => onContractChange({ contractCar: { ...contractCar, carNumber: e.target.value } })}
                            disabled
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>
            {/* 렌트사업자 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>렌트사업자</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="rentCorp.corpName"
                            value={contractCar?.rentCorp?.corpName || ""}
                            placeholder=""
                            onChange={(e) =>
                                onContractChange({
                                    contractCar: {
                                        ...contractCar,
                                        rentCorp: { ...contractCar.rentCorp, corpName: e.target.value },
                                    },
                                })
                            }
                            onInput={formatNumberWithCommas}
                            disabled
                        />
                    </div>
                </div>
            </div>
            {/* 본사 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>본사</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="rentCorp.address"
                            value={
                                contractCar?.rentCorp?.address
                                    ? `${contractCar?.rentCorp?.address} ${contractCar?.rentCorp?.addressDetail}`
                                    : ""
                            }
                            placeholder=""
                            onChange={(e) =>
                                onContractChange({
                                    contractCar: {
                                        ...contractCar,
                                        rentCorp: { ...contractCar.rentCorp, address: e.target.value },
                                    },
                                })
                            }
                            onInput={formatNumberWithCommas}
                            disabled
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            {/* 국산/수입 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>국산/수입</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={originOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="국산/수입"
                            value={originOptions.find((opt) => opt.value === contractCar?.cho)}
                            onChange={(option) => onContractChange({ contractCar: { ...contractCar, cho: option.value } })}
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 차종 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>차종</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            options={carGubunOptions}
                            isSearchable
                            placeholder="차종 선택"
                            value={carGubunOptions.find((opt) => opt.label === contractCar?.carGubun)}
                            onChange={(option) => onContractChange({ contractCar: { ...contractCar, carGubun: option.label } })}
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 제조사 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>제조사</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="제조사 선택"
                            options={manufacturerOptions}
                            value={manufacturerOptions.find((opt) => opt.value === contractCar?.bmNo)}
                            onChange={(option) =>
                                onContractChange({
                                    contractCar: {
                                        ...contractCar,
                                        bmNo: option.value,
                                        boiNo: "",
                                        boNo: "",
                                        bsNo: "",
                                        bdNo: "",
                                    },
                                })
                            }
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 모델 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>모델</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="모델 선택"
                            options={modelOptions}
                            value={modelOptions.find((opt) => opt.value === contractCar?.boiNo)}
                            onChange={(option) =>
                                onContractChange({
                                    contractCar: {
                                        ...contractCar,
                                        boiNo: option.value,
                                        boNo: "",
                                        bsNo: "",
                                        bdNo: "",
                                    },
                                })
                            }
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 상세모델 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>상세모델</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="상세모델 선택"
                            options={detailModelOptions}
                            value={detailModelOptions.find((opt) => opt.value === contractCar?.boNo)}
                            onChange={(option) =>
                                onContractChange({
                                    contractCar: { ...contractCar, boNo: option.value, bsNo: "", bdNo: "" },
                                })
                            }
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 등급 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>등급</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="등급 선택"
                            options={carClassOptions}
                            value={carClassOptions.find((opt) => opt.value === contractCar?.bsNo)}
                            onChange={(option) =>
                                onContractChange({
                                    contractCar: { ...contractCar, bsNo: option.value, bdNo: "" },
                                })
                            }
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 트림 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>트림</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="트림 선택"
                            options={carTrimOptions}
                            value={carTrimOptions.find((opt) => opt.value === contractCar?.bdNo)}
                            onChange={(option) => onContractChange({ contractCar: { ...contractCar, bdNo: option.value } })}
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 최초등록일 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>최초등록일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={contractCar?.firstDate}
                            onChange={(date) =>
                                onContractChange({ contractCar: { ...contractCar, firstDate: dateFormatDateTime(date, "YYYY-MM-DD") } })
                            }
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                            disabled
                        />
                    </div>
                </div>
            </div>
            {/* 외장색상 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>외장색상</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="color"
                            placeholder="화이트"
                            value={contractCar?.color || ""}
                            onChange={(e) => onContractChange({ contractCar: { ...contractCar, color: e.target.value } })}
                            disabled
                        />
                    </div>
                </div>
            </div>
            {/* 유종 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>유종</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={fuelOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="유종 선택"
                            value={fuelOptions.find((opt) => opt.label === contractCar?.fuel)}
                            onChange={(option) => onContractChange({ contractCar: { ...contractCar, fuel: option.label } })}
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 변속기 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>변속기</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={transmissionOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="변속기 선택"
                            value={transmissionOptions.find((opt) => opt.label === contractCar?.motorType)}
                            onChange={(option) => onContractChange({ contractCar: { ...contractCar, motorType: option.value } })}
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 배기량 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>배기량(cc)</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="배기량 선택"
                            options={displacementOptions}
                            value={displacementOptions.find((opt) => opt.label === contractCar?.displacement)}
                            onChange={(option) => onContractChange({ contractCar: { ...contractCar, displacement: option.value } })}
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            {/* 주행거리 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>주행거리(km)</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="mileage"
                            placeholder=""
                            value={contractCar?.mileage || ""}
                            onChange={(e) => onContractChange({ contractCar: { ...contractCar, mileage: e.target.value } })}
                            onInput={formatNumberWithCommas}
                        />
                        <span className="input-esset">km</span>
                    </div>
                </div>
            </div>
            {/* 유류량 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>유류량</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={fuelRateOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="유류량 선택"
                            value={fuelRateOptions.find((opt) => opt.value === contractCar?.gasCurrentAmount)}
                            onChange={(option) => onContractChange({ contractCar: { ...contractCar, gasCurrentAmount: option.value } })}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 운전자 정보 */
const DriverInfo = ({ formData, handleChange, setFormData, getFileName, handleCommonFileChange }) => {
    const mainDriver = formData.contractDrivers.find((driver) => driver.contractDriverType === "MAIN") || {};

    const updateDriverField = (value, driverType, fieldName) => {
        setFormData((prev) => {
            const updatedDrivers = prev.contractDrivers.map((driver) => {
                if (driver.contractDriverType === driverType) {
                    return { ...driver, [fieldName]: value };
                }
                return driver;
            });
            return { ...prev, contractDrivers: updatedDrivers };
        });
    };

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>고객이름</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="name"
                            value={mainDriver?.name || ""}
                            placeholder="고객이름"
                            onChange={(e) => updateDriverField(e.target.value, "MAIN", e.target.name)}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>주민번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="idNumberF"
                            value={mainDriver?.idNumberF || ""}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "MAIN", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={6}
                        />
                    </div>
                    <span className="text-esset">-</span>
                    <div className="form-element">
                        <input
                            type="text"
                            name="idNumberB"
                            value={mainDriver?.idNumberB || ""}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "MAIN", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={1}
                        />
                        <span className="input-esset">******</span>
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="phone"
                            value={mainDriver?.phone || ""}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "MAIN", e.target.name)}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허구분</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={driverTypeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="면허구분"
                            value={driverTypeOptions.find((opt) => opt.value === mainDriver?.driverLicenseType)}
                            onChange={(option) => updateDriverField(option.value, "MAIN", "driverLicenseType")}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="field-row">
                            <Select
                                options={localCodeOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                isSearchable={false}
                                placeholder="지역"
                                value={localCodeOptions.find((option) => option.value === mainDriver.localCode) || null}
                                onChange={(option) => updateDriverField(option.value, "MAIN", "localCode")}
                            />
                        </div>
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="driverLicenseNumber"
                            value={mainDriver?.driverLicenseNumber || ""}
                            placeholder="면허번호"
                            onChange={(e) => updateDriverField(e.target.value, "MAIN", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={10}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허 만기일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={mainDriver?.issueDate}
                            onChange={(date) => updateDriverField(date, "MAIN", "issueDate")}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="bCode"
                            value={mainDriver?.bCode || ""}
                            onChange={(e) => updateDriverField(e.target.value, "MAIN", e.target.name)}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "MAIN",
                                }));
                            }}
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="address"
                            value={mainDriver?.address || ""}
                            onChange={(e) => updateDriverField(e.target.value, "MAIN", e.target.name)}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "MAIN",
                                }));
                            }}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="addressDetail"
                            value={mainDriver?.addressDetail || ""}
                            onChange={(e) => updateDriverField(e.target.value, "MAIN", e.target.name)}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>
                        운전면허증 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("DRIVER_LICENSE_MAIN")}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => document.getElementById("driverLicenseInput_MAIN").click()}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 추가운전자 정보 */
const AdditionalDriverInfo = ({ formData, handleChange, setFormData, getFileName, handleCommonFileChange }) => {
    const additionalDriver = formData.contractDrivers.find((driver) => driver.contractDriverType === "ADDITIONAL") || {};

    const updateDriverField = (value, driverType, fieldName) => {
        setFormData((prev) => {
            const updatedDrivers = prev.contractDrivers.map((driver) => {
                if (driver.contractDriverType === driverType) {
                    return { ...driver, [fieldName]: value };
                }
                return driver;
            });
            return { ...prev, contractDrivers: updatedDrivers };
        });
    };

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>고객이름</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="name"
                            value={additionalDriver?.name || ""}
                            placeholder="고객이름"
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>주민번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="idNumberF"
                            value={additionalDriver?.idNumberF || ""}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={6}
                        />
                    </div>
                    <span className="text-esset">-</span>
                    <div className="form-element">
                        <input
                            type="text"
                            name="idNumberB"
                            value={additionalDriver?.idNumberB || ""}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={1}
                        />
                        <span className="input-esset">******</span>
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="phone"
                            value={additionalDriver?.phone}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허구분</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={driverTypeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="면허구분"
                            value={driverTypeOptions.find((opt) => opt.value === additionalDriver?.driverLicenseType)}
                            onChange={(option) => updateDriverField(option.value, "ADDITIONAL", "driverLicenseType")}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="field-row">
                            <Select
                                options={localCodeOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                isSearchable={false}
                                placeholder="지역"
                                value={localCodeOptions.find((option) => option.value === additionalDriver.localCode) || null}
                                onChange={(option) => updateDriverField(option.value, "ADDITIONAL", "localCode")}
                            />
                        </div>
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="driverLicenseNumber"
                            value={additionalDriver?.driverLicenseNumber || ""}
                            placeholder="면허번호"
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허 만기일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={additionalDriver?.issueDate}
                            onChange={(date) => updateDriverField(date, "ADDITIONAL", "issueDate")}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="bCode"
                            value={additionalDriver?.bCode || ""}
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "ADDITIONAL",
                                }));
                            }}
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="address"
                            value={additionalDriver?.address || ""}
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "ADDITIONAL",
                                }));
                            }}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="addressDetail"
                            value={additionalDriver?.addressDetail || ""}
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>
                        운전면허증 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("DRIVER_LICENSE_SUB")}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => document.getElementById("driverLicenseInput_ADDITIONAL").click()}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 사고차량 정보 */
const AccidentInfo = ({ formData, handleChange, setFormData }) => {
    const { accidentCar } = formData;

    // 차종 옵션 (독립)
    const { data: carGubunData } = useGetCarGubunQuery();
    const carGubunOptions =
        carGubunData && carGubunData.data
            ? carGubunData.data.map((item) => ({
                  value: item.bgNo,
                  label: item.bgName,
              }))
            : [];

    // 제조사
    const { data: manufacturerData } = useGetCarManufacturerQuery({ bmNo: null });
    const manufacturerOptions =
        manufacturerData?.data?.map((item) => ({
            value: item.bmNo,
            label: item.bmName,
        })) || [];

    // 모델 (skip: !manufacturer)
    const { data: modelData } = useGetCarModelQuery({ bmNo: accidentCar?.bmNo }, { skip: !accidentCar?.bmNo });
    const modelOptions =
        modelData?.data?.map((item) => ({
            value: item.boiNo,
            label: item.boiName,
        })) || [];

    // 상세모델 (skip: !model)
    const { data: detailModelData } = useGetCarModelDetailQuery({ boiNo: accidentCar?.boiNo }, { skip: !accidentCar?.boiNo });
    const detailModelOptions =
        detailModelData?.data?.map((item) => ({
            value: item.boNo,
            label: item.boName,
        })) || [];

    // 등급 (skip: !detailModel)
    const { data: carClassData } = useGetCarClassQuery({ boNo: accidentCar?.boNo }, { skip: !accidentCar?.boNo });
    const carClassOptions =
        carClassData?.data?.map((item) => ({
            value: item.bsNo,
            label: item.bsName,
        })) || [];

    // 트림 (skip: !carClass)
    const { data: carTrimData } = useGetCarTrimQuery({ bsNo: accidentCar?.bsNo }, { skip: !accidentCar?.bsNo });
    const carTrimOptions =
        carTrimData?.data?.map((item) => ({
            value: item.bdNo,
            label: item.bdName,
        })) || [];

    // 유종 옵션 : getFuelList API
    const { data: fuelData } = useGetFuelListQuery();
    const fuelOptions =
        fuelData && fuelData.data
            ? fuelData.data.map((item) => ({
                  value: item.bfNo,
                  label: item.bfName,
              }))
            : [];

    return (
        <>
            {/* 차량번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>차량번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="accidentCar.carNumber"
                            placeholder=""
                            value={accidentCar?.carNumber || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>

            {/* 국산/수입 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>국산/수입</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={originOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="국산/수입 선택"
                            value={originOptions.find((opt) => opt.value === accidentCar?.cho)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        cho: option.value,
                                    },
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 차종 (독립) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>차종</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            options={carGubunOptions}
                            isSearchable
                            placeholder="차종 선택"
                            value={carGubunOptions.find((opt) => opt.label === accidentCar?.carGubun)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        carGubun: option.label,
                                    },
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 제조사 (독립; 제조사 선택 시 하위 초기화) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>제조사</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="제조사 선택"
                            options={manufacturerOptions}
                            value={manufacturerOptions?.find((opt) => opt.value === accidentCar?.bmNo)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        bmNo: option.value,
                                        boiNo: "",
                                        boNo: "",
                                        bsNo: "",
                                        bdNo: "",
                                    },
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 모델 (제조사 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>모델</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="모델 선택"
                            options={modelOptions}
                            value={modelOptions?.find((opt) => opt.value === accidentCar?.boiNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        boiNo: option.value,
                                        boNo: "",
                                        bsNo: "",
                                        bdNo: "",
                                    },
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 상세모델 (모델 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>상세모델</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="상세모델 선택"
                            options={detailModelOptions}
                            value={detailModelOptions?.find((opt) => opt.value === accidentCar?.boNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        boNo: option.value,
                                        bsNo: "",
                                        bdNo: "",
                                    },
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 등급 (상세모델 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>등급</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="등급 선택"
                            options={carClassOptions}
                            value={carClassOptions?.find((opt) => opt.value === accidentCar?.bsNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        bsNo: option.value,
                                        bdNo: "",
                                    },
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 트림 (등급 선택에 종속) */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>트림</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="트림 선택"
                            options={carTrimOptions}
                            value={carTrimOptions?.find((opt) => opt.value === accidentCar?.bdNo)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        bdNo: option.value,
                                    },
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 연식 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>연식</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={accidentCar?.firstDate}
                            onChange={(date) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...accidentCar,
                                        firstDate: dateFormatDateTime(date, "YYYY-MM-DD"),
                                    },
                                }));
                            }}
                            dateFormat="yyyy.MM.dd"
                            onKeyDown={(e) => e.preventDefault()}
                        />
                    </div>
                </div>
            </div>

            {/* 배기량 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>배기량(cc)</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="배기량 선택"
                            options={displacementOptions}
                            value={displacementOptions?.find((opt) => opt.value === accidentCar?.displacement)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    accidentCar: {
                                        ...prev.accidentCar,
                                        displacement: option.value,
                                    },
                                }))
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 기타 정보 */
const OtherInfo = ({ formData, handleChange, setFormData }) => {
    const { agentePartner, salesPartner } = formData;
    const { data: agentPartnerData, agentRefetch } = useGetAgentePartnerListQuery();
    const { data: salesPartnerData, salesRefetch } = useGetSalesPartnerListQuery();

    const agentPartnerOptions =
        agentPartnerData && agentPartnerData.data
            ? agentPartnerData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : [];

    const salesPartnerOptions =
        salesPartnerData && salesPartnerData.data
            ? salesPartnerData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : [];

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>소개처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={agentPartnerOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="소개처 선택"
                            value={agentPartnerOptions.find((opt) => opt.label === agentePartner)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    agentePartner: option.label,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>영업자</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={salesPartnerOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="영업자 선택"
                            value={salesPartnerOptions.find((opt) => opt.label === salesPartner)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    salesPartner: option.label,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>
        </>
    );
};

/* 기타 특이사항 */
const OtherEtcInfo = ({ formData, handleChange, setFormData }) => {
    const { memoForClient } = formData;

    return (
        <>
            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea
                            name="memoForClient"
                            placeholder="메모를 작성해주세요."
                            value={memoForClient || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 특이사항 */
const EtcInfo = ({ formData, handleChange, setFormData }) => {
    const { memoForContract } = formData;

    return (
        <>
            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea
                            name="memoForContract"
                            placeholder="메모를 작성해주세요."
                            value={memoForContract || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 보험사 전달사항 */
const InsuranceDeliveredInfo = ({ formData, handleChange, setFormData }) => {
    const { memoForInsCorp } = formData;

    return (
        <>
            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea
                            name="memoForInsCorp"
                            placeholder="메모를 작성해주세요."
                            value={memoForInsCorp || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 보험 정보 */
const InsuranceInfo = ({ formData, handleChange, setFormData }) => {
    const { contractInsurance, contractMechanic } = formData;

    // API 호출: 보험사 업체 조회
    const { data: insuranceCorpData } = useGetInsuranceCorpQuery();
    const insuranceCorpOptions =
        insuranceCorpData && insuranceCorpData.data
            ? insuranceCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : [];

    return (
        <>
            {/* 보험사 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>보험사</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={insuranceCorpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={true}
                            placeholder="보험사 선택"
                            value={insuranceCorpOptions.find((opt) => opt.label === contractInsurance?.insuranceCorpName)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    contractInsurance: {
                                        ...prev.contractInsurance,
                                        insuranceCorpName: option.label,
                                    },
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>접수번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.receiptNumber"
                            value={contractInsurance?.receiptNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>담당자</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.managerName"
                            value={contractInsurance?.managerName || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>담당자 연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.managerPhone"
                            value={contractInsurance?.managerPhone || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>팩스번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.faxNumber"
                            value={contractInsurance?.faxNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>이메일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.email"
                            value={contractInsurance?.email || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            {/* 공업사 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>공업사</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractMechanic.mechanicCorpName"
                            value={contractMechanic?.mechanicCorpName || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                        {/* <Select
                            options={insuranceCorpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="공업사 선택"
                            value={insuranceCorpOptions.find((opt) => opt.value === contractMechanic?.mechanicCorpName)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    contractMechanic: {
                                        ...prev.contractMechanic,
                                        mechanicCorpName: option.value,
                                    },
                                }));
                            }}
                        /> */}
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>공업사 연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractMechanic.phone"
                            value={contractMechanic?.phone || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>
        </>
    );
};

/* 배차/반납 정보 */
const ReturnInfo = ({ allocation, onAllocationChange }) => {
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [addressTarget, setAddressTarget] = useState(""); // "release" or "return"

    const releaseMethodOptions = [
        { value: "직접진행", label: "직접진행" },
        { value: "탁송진행", label: "탁송진행" },
        { value: "고객내방", label: "고객내방" },
    ];
    const returnMethodOptions = [
        { value: "직접진행", label: "직접진행" },
        { value: "탁송진행", label: "탁송진행" },
        { value: "고객내방", label: "고객내방" },
    ];

    // 주소 검색 모달에서 선택된 주소 데이터를 처리하는 콜백
    const handleAddressSelect = (data) => {
        if (addressTarget === "release") {
            onAllocationChange({ releasebCode: data.bcode, releaseAddress: data.address });
        } else if (addressTarget === "return") {
            onAllocationChange({ returnbCode: data.bcode, returnAddress: data.address });
        }
        setIsAddressModalOpen(false);
        setAddressTarget("");
    };

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>배차일시</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={allocation?.releasedAt ? new Date(allocation?.releasedAt) : null}
                            onChange={(date) => onAllocationChange({ releasedAt: date })}
                            showTimeSelect
                            timeIntervals={30}
                            timeCaption="시간"
                            dateFormat="yyyy.MM.dd HH:mm"
                            onKeyDown={(e) => e.preventDefault()}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>배차방법</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={releaseMethodOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="배차방법"
                            value={releaseMethodOptions.find((opt) => opt.value === allocation?.releaseMethod)}
                            onChange={(option) => onAllocationChange({ releaseMethod: option.value })}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>배차주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="releasebCode"
                            value={allocation?.releasebCode || ""}
                            readOnly
                            onClick={() => {
                                setAddressTarget("release");
                                setIsAddressModalOpen(true);
                            }}
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="releaseAddress"
                            value={allocation?.releaseAddress || ""}
                            readOnly
                            onClick={() => {
                                setAddressTarget("release");
                                setIsAddressModalOpen(true);
                            }}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="releaseAddressDetail"
                            value={allocation?.releaseAddressDetail || ""}
                            onChange={(e) => onAllocationChange({ releaseAddressDetail: e.target.value })}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>반납일시</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <DatePicker
                            locale={ko}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={20}
                            selected={allocation?.returnedAt ? new Date(allocation?.returnedAt) : null}
                            onChange={(date) => onAllocationChange({ returnedAt: date })}
                            showTimeSelect
                            timeIntervals={30}
                            timeCaption="시간"
                            dateFormat="yyyy.MM.dd HH:mm"
                            onKeyDown={(e) => e.preventDefault()}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>반납방법</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={returnMethodOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="반납방법"
                            value={returnMethodOptions.find((opt) => opt.value === allocation?.returnMethod)}
                            onChange={(option) => onAllocationChange({ returnMethod: option.value })}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>반납주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="returnbCode"
                            value={allocation?.returnbCode || ""}
                            readOnly
                            onClick={() => {
                                setAddressTarget("return");
                                setIsAddressModalOpen(true);
                            }}
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="returnAddress"
                            value={allocation?.returnAddress || ""}
                            readOnly
                            onClick={() => {
                                setAddressTarget("return");
                                setIsAddressModalOpen(true);
                            }}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="returnAddressDetail"
                            value={allocation?.returnAddressDetail || ""}
                            onChange={(e) => onAllocationChange({ returnAddressDetail: e.target.value })}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea
                            name="memo"
                            placeholder=""
                            value={allocation?.memo || ""}
                            onChange={(e) => onAllocationChange({ memo: e.target.value })}
                        />
                    </div>
                </div>
            </div>
            {isAddressModalOpen && (
                <SearchAddress modalStatus={isAddressModalOpen} setModalStatus={setIsAddressModalOpen} setAddress={handleAddressSelect} />
            )}
        </>
    );
};

/* 사업자 정보 */
const BusinessInfo = ({ formData, handleChange, setFormData, getFileName, handleCommonFileChange }) => {
    const { contractBusinessInfo } = formData;

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 이름</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="contractBusinessInfo.corpName"
                            value={contractBusinessInfo?.corpName || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="contractBusinessInfo.bizNumber"
                            value={contractBusinessInfo?.bizNumber || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>담당자 이름</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="contractBusinessInfo.managerName"
                            value={contractBusinessInfo?.managerName || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>담당자 연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="contractBusinessInfo.managerPhone"
                            value={contractBusinessInfo?.managerPhone || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>이메일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder=""
                            name="contractBusinessInfo.email"
                            value={contractBusinessInfo?.email || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>사업자 주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="contractBusinessInfo.bCode"
                            value={contractBusinessInfo?.bCode}
                            onChange={handleChange}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "BUSINESS",
                                }));
                            }}
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="contractBusinessInfo.address"
                            value={contractBusinessInfo?.address}
                            onChange={handleChange}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                    addressPopupTarget: "BUSINESS",
                                }));
                            }}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="contractBusinessInfo.addressDetail"
                            value={contractBusinessInfo?.addressDetail}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>사업자등록증</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("BIZ_LIC")}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => document.getElementById("bizLicenseInput").click()}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

/* 보험 정보 테이블 */
const InsuranceTable = ({ insuranceItems, onUpdateInsuranceItem }) => {
    // insuranceItems는 개별 계약서의 insuranceItems 배열입니다.
    const handleToggleJoinYn = (index, currentJoinYn) => {
        const nextJoinYn = currentJoinYn === "Y" ? "N" : "Y";
        onUpdateInsuranceItem(index, { joinYn: nextJoinYn });
    };

    return (
        <>
            <div className="table-container">
                <div className="table-content">
                    <div className="table-wrapper">
                        <div className="table">
                            <table>
                                <colgroup>
                                    <col width="25%" />
                                    <col width="25%" />
                                    <col width="25%" />
                                    <col width="25%" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">가입 여부</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">종류</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">보상범위</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">자부담금</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {insuranceItems?.map((item, idx) => {
                                        const config = INSURANCE_ITEM_CONFIGS[item.insuranceItemType];
                                        if (!config) return null;
                                        return (
                                            <tr key={item.insuranceItemType}>
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <button
                                                            type="button"
                                                            className={`btn-toggle-own ${item.joinYn === "Y" ? "active" : ""}`}
                                                            onClick={() => handleToggleJoinYn(idx, item.joinYn)}
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <span className="cell-desc color-main">{config.label}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <span className="cell-input">
                                                            {item.insuranceItemType === "SELF_CAR_DAMAGE" ? (
                                                                <>
                                                                    <Select
                                                                        className="custom-select"
                                                                        classNamePrefix="custom-select"
                                                                        isSearchable={false}
                                                                        placeholder="구분"
                                                                        value={
                                                                            item.selfCarDamageRangeType
                                                                                ? selfCarDamageTypeOptions.find(
                                                                                      (opt) => opt.value === item.selfCarDamageRangeType
                                                                                  )
                                                                                : null
                                                                        }
                                                                        onChange={(option) => {
                                                                            onUpdateInsuranceItem(idx, {
                                                                                selfCarDamageRangeType: option.value,
                                                                                compensationRange: null,
                                                                                selfCarDamageRateType: null,
                                                                            });
                                                                        }}
                                                                        options={selfCarDamageTypeOptions}
                                                                    />
                                                                    {item.selfCarDamageRangeType === "PRICE" && (
                                                                        <Select
                                                                            className="custom-select"
                                                                            classNamePrefix="custom-select"
                                                                            isSearchable={false}
                                                                            placeholder="금액"
                                                                            value={
                                                                                item.compensationRange
                                                                                    ? selfCarDamageAmountOptions.find(
                                                                                          (opt) => opt.value === item.compensationRange
                                                                                      )
                                                                                    : null
                                                                            }
                                                                            onChange={(option) => {
                                                                                onUpdateInsuranceItem(idx, {
                                                                                    compensationRange: option.value,
                                                                                    selfCarDamageRateType: null,
                                                                                });
                                                                            }}
                                                                            options={selfCarDamageAmountOptions}
                                                                        />
                                                                    )}
                                                                    {item.selfCarDamageRangeType === "RATE" && (
                                                                        <Select
                                                                            className="custom-select"
                                                                            classNamePrefix="custom-select"
                                                                            isSearchable={false}
                                                                            placeholder="비율"
                                                                            value={
                                                                                item.selfCarDamageRateType
                                                                                    ? selfCarDamageRateOptions.find(
                                                                                          (opt) => opt.value === item.selfCarDamageRateType
                                                                                      )
                                                                                    : null
                                                                            }
                                                                            onChange={(option) => {
                                                                                onUpdateInsuranceItem(idx, {
                                                                                    selfCarDamageRateType: option.value,
                                                                                });
                                                                            }}
                                                                            options={selfCarDamageRateOptions}
                                                                        />
                                                                    )}
                                                                </>
                                                            ) : item.insuranceItemType === "DAEIN_PAY_1" ? (
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="보상범위"
                                                                    value={
                                                                        item.limitType
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.limitType
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        onUpdateInsuranceItem(idx, {
                                                                            limitType:
                                                                                typeof option.value === "string" ? option.value : null,
                                                                            compensationRange:
                                                                                typeof option.value === "number" ? option.value : null,
                                                                        });
                                                                    }}
                                                                />
                                                            ) : item.insuranceItemType === "DAEIN_PAY_2" ? (
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="보상범위"
                                                                    value={
                                                                        item.limitType === "UN_LIMIT"
                                                                            ? config.coverageOptions.find((opt) => opt.value === "UN_LIMIT")
                                                                            : config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                    }
                                                                    onChange={(option) => {
                                                                        if (option.value === "UN_LIMIT") {
                                                                            onUpdateInsuranceItem(idx, {
                                                                                limitType: "UN_LIMIT",
                                                                                compensationRange: null,
                                                                            });
                                                                        } else {
                                                                            onUpdateInsuranceItem(idx, {
                                                                                limitType: null,
                                                                                compensationRange: Number(option.value),
                                                                            });
                                                                        }
                                                                    }}
                                                                />
                                                            ) : item.insuranceItemType === "EMERGENCY_SERVICE" ? (
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="연 횟수"
                                                                    value={
                                                                        item.compensationRange
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        onUpdateInsuranceItem(idx, {
                                                                            limitType: null,
                                                                            compensationRange: option.value,
                                                                        });
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="보상범위"
                                                                    value={
                                                                        item.compensationRange
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        onUpdateInsuranceItem(idx, {
                                                                            limitType: null,
                                                                            compensationRange: Number(option.value),
                                                                        });
                                                                    }}
                                                                />
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="cell" data-align="left">
                                                        <span className="cell-input">
                                                            <input
                                                                type="number"
                                                                placeholder="0"
                                                                value={item.selfPay || ""}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    onUpdateInsuranceItem(idx, { selfPay: val ? Number(val) : null });
                                                                }}
                                                            />
                                                            <span className="input-esset">
                                                                {item.insuranceItemType === "EMERGENCY_SERVICE" ? "km" : "만원"}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/* 캐피탈 정보 */
const CapitalInfo = ({ formData, handleChange, setFormData }) => {
    const { insuranceAge } = formData;

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>캐피탈 업체</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={insuranceAgeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={true}
                            placeholder="캐피탈 업체"
                            value={insuranceAgeOptions.find((opt) => opt.value === insuranceAge)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    insuranceAge: option.value,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>대출 총액</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" placeholder="" name="repName" value={formData.repName || ""} onChange={handleChange} />
                        <span className="input-esset">원</span>
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>적용 금리</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" placeholder="" name="repName" value={formData.repName || ""} onChange={handleChange} />
                        <span className="input-esset">%</span>
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>할부 기간</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={insuranceAgeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="할부 기간"
                            value={insuranceAgeOptions.find((opt) => opt.value === insuranceAge)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    insuranceAge: option.value,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>월 할부금</span>
                </div>
                <div className="row-content ">
                    <div className="form-element ">
                        <input type="text" placeholder="" name="repName" value={formData.repName || ""} onChange={handleChange} disabled />
                        <span className="input-esset">원</span>
                    </div>
                </div>
            </div>
        </>
    );
};

const BillingInfo = ({ formData, setFormData, contract, onContractChange, setSelectedContractIndex }) => {
    const { totalOrderCharge } = formData;
    const { allocation, billingInfo } = contract;

    useEffect(() => {
        if (
            billingInfo &&
            billingInfo?.rentPrice &&
            billingInfo?.rentPrice.rentPriceItemDetails &&
            allocation?.releasedAt &&
            allocation?.returnedAt &&
            totalOrderCharge?.chargeRate
        ) {
            try {
                const { days, hours } = calculateRentalDuration(allocation?.releasedAt, allocation?.returnedAt);
                const result = calculateRentPrice(
                    billingInfo?.rentPrice.rentPriceItemDetails,
                    new Date(allocation?.releasedAt),
                    new Date(allocation?.returnedAt),
                    totalOrderCharge.chargeRate
                );
                const formulaStr = `총 대여료: ${comma(result.rentFee)}원, 보험 청구: ${comma(
                    result.insuranceChargeFee
                )}원, 플랫폼 수수료: ${comma(result.platformCommissionFee)}원`;
                onContractChange({
                    totalRentalPeriod: `${days}일 ${hours}시간`,
                    days,
                    hours,
                    rentFee: result.rentFee,
                    insuranceChargeFee: result.insuranceChargeFee,
                    platformCommissionFee: result.platformCommissionFee,
                    exceptCommissionFee: result.exceptCommissionFee,
                    exceptAddTaxFee: result.exceptAddTaxFee,
                    orderCommissionFee: result.orderCommissionFee,
                    obtainEarningFee: result.obtainEarningFee,
                    chargeFee: comma(result.chargeFee),
                    formula: formulaStr,
                });
            } catch (error) {
                console.error("청구수식 계산 오류:", error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        totalOrderCharge?.chargeMethod,
        totalOrderCharge?.chargeRate,
        allocation?.releasedAt,
        allocation?.returnedAt,
        formData.billingSettingPopupStatus,
    ]);

    return (
        <>
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>대여기간</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="totalRentalPeriod"
                            placeholder=""
                            value={billingInfo?.totalRentalPeriod || ""}
                            onChange={(e) => onContractChange({ billingInfo: { ...billingInfo, totalRentalPeriod: e.target.value } })}
                            disabled
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>청구율</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={chargeRateOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable
                            placeholder="청구율"
                            value={chargeRateOptions?.find((opt) => opt.value === totalOrderCharge?.chargeRate)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    totalOrderCharge: {
                                        ...prev.totalOrderCharge,
                                        chargeRate: option.value,
                                    },
                                }));
                            }}
                            isDisabled
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>청구수식</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="modelTitle"
                            placeholder=""
                            value={billingInfo?.rentPrice?.modelTitle || ""}
                            onChange={(e) => onContractChange({ billingInfo: { ...billingInfo, formula: e.target.value } })}
                            disabled
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>청구 요금</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="chargeFee"
                            placeholder=""
                            value={billingInfo?.chargeFee || ""}
                            onChange={(e) => onContractChange({ billingInfo: { ...billingInfo, chargeFee: e.target.value } })}
                            onInput={formatNumberWithCommas}
                            disabled
                        />
                        <span className="input-esset">원</span>
                    </div>
                </div>
            </div>
        </>
    );
};

const TotalBillingInfo = ({ formData, handleChange, setFormData, passedOrderId }) => {
    const { contracts, totalOrderCharge } = formData;

    useEffect(() => {
        let totalDays = 0;
        let totalHours = 0;
        let totalRentFee = 0;
        let totalChargeFee = 0;

        if (Array.isArray(contracts) && contracts.length > 0) {
            contracts.forEach((contract) => {
                const { allocation, billingInfo } = contract;
                // 대여기간 계산: allocation.releasedAt와 allocation.returnedAt이 있을 때
                if (allocation && allocation?.releasedAt && allocation?.returnedAt) {
                    const { days, hours } = calculateRentalDuration(new Date(allocation?.releasedAt), new Date(allocation?.returnedAt));
                    totalDays += days;
                    totalHours += hours;
                }
                // 렌트 요금: billingInfo.rentFee
                if (billingInfo && billingInfo.rentFee) {
                    totalRentFee += Number(uncomma(billingInfo.rentFee)) || 0;
                }
                // 청구 요금: billingInfo.chargeFee
                if (billingInfo && billingInfo.chargeFee) {
                    totalChargeFee += Number(uncomma(billingInfo.chargeFee)) || 0;
                }
            });

            // 만약 총 시간(totalHours)이 24시간 이상이면 추가 일수로 변환
            if (totalHours >= 24) {
                const extraDays = Math.floor(totalHours / 24);
                totalDays += extraDays;
                totalHours = totalHours % 24;
            }

            // formData.totalOrderCharge 결과 세팅 (totalOrderCharge가 기존에 없으면 새 객체 생성)
            setFormData((prev) => ({
                ...prev,
                totalOrderCharge: {
                    ...prev.totalOrderCharge,
                    totalRentalPeriod: `${totalDays}일 ${totalHours}시간`,
                    days: totalDays,
                    hours: totalHours,
                    totalRentFee,
                    totalChargeFee,
                },
            }));
        }
    }, [contracts, setFormData]);

    // 요금표 목록 조회
    const { data: rentPriceTableData } = useGetRentPriceTableQuery();
    const rentPriceTableOptions = [
        { value: "INSURANCE_BASE", label: "보험사기준" },
        ...(rentPriceTableData && rentPriceTableData.data
            ? rentPriceTableData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : []),
        { value: "INPERSON", label: "직접입력" },
    ];

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>청구 방법</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={rentPriceTableOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable
                            placeholder="청구 방법"
                            value={rentPriceTableOptions?.find((opt) => opt.value === totalOrderCharge?.chargeMethod)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    totalOrderCharge: {
                                        ...prev.totalOrderCharge,
                                        chargeMethod: option.value,
                                    },
                                }));
                            }}
                            isDisabled={passedOrderId}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>청구율</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={chargeRateOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable
                            placeholder="청구율"
                            value={chargeRateOptions?.find((opt) => opt.value === totalOrderCharge?.chargeRate)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    totalOrderCharge: {
                                        ...prev.totalOrderCharge,
                                        chargeRate: option.value,
                                    },
                                }));
                            }}
                            isDisabled={passedOrderId}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            {!passedOrderId && (
                <>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>총 렌트 시간</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    name="totalRentalPeriod"
                                    placeholder=""
                                    value={totalOrderCharge?.totalRentalPeriod || ""}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>총 렌트 요금</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    name="totalRentFee"
                                    placeholder=""
                                    value={totalOrderCharge?.totalRentFee ? comma(totalOrderCharge.totalRentFee) : ""}
                                    disabled
                                />
                                <span className="input-esset">원</span>
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>총 청구 요금</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    name="totalChargeFee"
                                    placeholder=""
                                    value={totalOrderCharge?.totalChargeFee ? comma(totalOrderCharge.totalChargeFee) : ""}
                                    disabled
                                />
                                <span className="input-esset">원</span>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

// 약관 확인
const TermsFile = ({ formData, setFormData, getFileName, getTermFileName, handleCommonFileChange }) => {
    // formData.contractTerms가 없으면 빈 배열로 초기화
    const terms = formData.contractTerms || [];

    // 약관명 변경 처리
    const handleTermNameChange = (index, newName) => {
        setFormData((prev) => {
            const updated = [...(prev.contractTerms || [])];
            updated[index] = { ...updated[index], name: newName };
            return { ...prev, contractTerms: updated };
        });
    };

    // 약관 삭제 처리
    const handleDeleteTerm = (index) => {
        setFormData((prev) => ({
            ...prev,
            contractTerms: (prev.contractTerms || []).filter((_, i) => i !== index),
        }));
    };

    return (
        <>
            {terms?.map((term, idx) => (
                <div className="item-row" data-grow="1" key={idx}>
                    <div className="row-head">
                        <span>추가 약관</span>
                    </div>
                    <div className="row-content">
                        {/* 약관명 입력 */}
                        <div className="form-element">
                            <input
                                type="text"
                                name="name"
                                value={term.name || ""}
                                placeholder="약관명"
                                onChange={(e) => handleTermNameChange(idx, e.target.value)}
                            />
                        </div>
                        {/* 업로드 파일명 표시 (업로드 API로 받은 파일 id는 handleCommonFileChange에서 처리) */}
                        <div className="form-element">
                            <input
                                type="text"
                                readOnly
                                value={getTermFileName(idx)}
                                placeholder={term.upload.fileName || "4MB 이하 PDF 파일로 첨부해주세요."}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
};

// 첨부파일 등록
const AttachedFile = ({ formData, handleChange, setFormData, getFileName, handleCommonFileChange }) => {
    return (
        <>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>첨부파일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" readOnly value={getFileName("ADD_FILE")} placeholder="4MB 이하 파일로 첨부해주세요." />
                    </div>
                </div>
            </div>
        </>
    );
};

// 소개처 선택
const AgentPartnersPopup = ({ layer = true, onClose, formData, setFormData, onAgentPartnerSelect }) => {
    const [query, setQuery] = useState("");
    const [selectedPartner, setSelectedPartner] = useState(null);

    const { data: partnerData, refetch } = useGetAgentePartnerListQuery();

    const handleSearch = () => {
        refetch();
    };

    const handleSelectPartner = (vehicle) => {
        setSelectedPartner(vehicle);
    };

    const handleConfirm = () => {
        if (!selectedPartner) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "소개처를 선택하세요.",
            }));
            return;
        }
        onAgentPartnerSelect(selectedPartner);
        onClose();
    };

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup service-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">소개처 선택</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="table-container">
                        <div className="table-filter">
                            <div className="filter-row">
                                <div className="filter-item">
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="입력"
                                                value={query || ""}
                                                onChange={(e) => setQuery(e.target.value)}
                                            />
                                        </div>
                                        <div className="item-buttons">
                                            <button type="button" className="btn-search" onClick={handleSearch}>
                                                검색
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="table-content">
                            <div className="table-wrapper">
                                <div className="table">
                                    <table>
                                        <colgroup>
                                            <col width={"12%"} />
                                            <col width={"37%"} />
                                            <col width={"24%"} />
                                            <col width={"27%"} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">선택</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">영업자명</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">담당자</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">연락처</div>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {partnerData && partnerData.data && partnerData.data.length > 0 ? (
                                                partnerData.data.map((partner) => (
                                                    <tr key={partner.id}>
                                                        <td>
                                                            <div className="cell" style={{ height: "100%" }}>
                                                                <div className="input-checkbox">
                                                                    <div className="checkbox" style={{ display: "flex" }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="selectedPartner"
                                                                            id={`selectedPartner-${partner.id}`}
                                                                            checked={selectedPartner?.id === partner.id}
                                                                            onChange={() => handleSelectPartner(partner)}
                                                                        />
                                                                        <label htmlFor={`selectedPartner-${partner.id}`}></label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">
                                                                    {partner.address} {partner.addressDetail}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{partner.name}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{partner.phone}</div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4">소개처 데이터가 없습니다.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button type="button" className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button type="button" className="btn btn-primary btn-confirm" onClick={handleConfirm}>
                        완료
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

// 영업자 선택
const SalesPartnersPopup = ({ layer = true, onClose, formData, setFormData, onSalesPartnerSelect }) => {
    const [query, setQuery] = useState("");
    const [selectedPartner, setSelectedPartner] = useState(null);

    const { data: partnerData, refetch } = useGetSalesPartnerListQuery();

    const handleSearch = () => {
        refetch();
    };

    const handleSelectPartner = (vehicle) => {
        setSelectedPartner(vehicle);
    };

    const handleConfirm = () => {
        if (!selectedPartner) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "영업자를 선택하세요.",
            }));
            return;
        }
        onSalesPartnerSelect(selectedPartner);
        onClose();
    };

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup service-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">영업자 선택</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="table-container">
                        <div className="table-filter">
                            <div className="filter-row">
                                <div className="filter-item">
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="입력"
                                                value={query || ""}
                                                onChange={(e) => setQuery(e.target.value)}
                                            />
                                        </div>
                                        <div className="item-buttons">
                                            <button type="button" className="btn-search" onClick={handleSearch}>
                                                검색
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="table-content">
                            <div className="table-wrapper">
                                <div className="table">
                                    <table>
                                        <colgroup>
                                            <col width={"12%"} />
                                            <col width={"37%"} />
                                            <col width={"24%"} />
                                            <col width={"27%"} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">선택</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">영업자명</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">담당자</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">연락처</div>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {partnerData && partnerData.data && partnerData.data.length > 0 ? (
                                                partnerData.data.map((partner) => (
                                                    <tr key={partner.id}>
                                                        <td>
                                                            <div className="cell" style={{ height: "100%" }}>
                                                                <div className="input-checkbox">
                                                                    <div className="checkbox" style={{ display: "flex" }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="selectedPartner"
                                                                            id={`selectedPartner-${partner.id}`}
                                                                            checked={selectedPartner?.id === partner.id}
                                                                            onChange={() => handleSelectPartner(partner)}
                                                                        />
                                                                        <label htmlFor={`selectedPartner-${partner.id}`}></label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">
                                                                    {partner.address} {partner.addressDetail}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{partner.name}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{partner.phone}</div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4">영업자 데이터가 없습니다.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button type="button" className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button type="button" className="btn btn-primary btn-confirm" onClick={handleConfirm}>
                        완료
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

// 차량 선택
const VehicleListPopup = ({
    layer = true,
    onClose,
    setFormData,
    rentCorpId, // 현재 선택된 계약서의 rentCorpId
    onRentCorpSelect, // 계약서의 rentCorpId를 업데이트하는 콜백
    onVehicleSelect, // 선택한 차량 데이터를 부모로 전달하는 콜백
}) => {
    const [query, setQuery] = useState("");
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    // 사업자 리스트 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData && myRentCorpData.data
            ? myRentCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    const { data: carsData, refetch } = useGetAllCarsQuery({ rent_corp_id: rentCorpId, query }, { skip: !rentCorpId });

    const handleSearch = () => {
        if (!rentCorpId) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자를 먼저 선택하세요.",
            }));
            return;
        }
        refetch();
    };

    const handleSelectVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
    };

    const handleConfirm = () => {
        if (!selectedVehicle) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "차량을 선택하세요.",
            }));
            return;
        }
        onVehicleSelect(selectedVehicle);
        onClose();
    };

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>
            <div className="popup vehicle-list-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">차량 선택</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="table-container">
                        <div className="table-filter">
                            <div className="filter-row">
                                <div className="filter-item">
                                    <div className="item-content">
                                        <div className="item-input">
                                            {/* 사업자 선택: 선택 시 onRentCorpSelect 호출 */}
                                            <Select
                                                options={corpOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="사업자 선택"
                                                value={corpOptions.find((opt) => opt.value === rentCorpId)}
                                                onChange={(option) => onRentCorpSelect(option.value)}
                                            />
                                        </div>
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="차량번호 입력"
                                                value={query || ""}
                                                onChange={(e) => setQuery(e.target.value)}
                                            />
                                        </div>
                                        <div className="item-buttons">
                                            <button type="button" className="btn-search" onClick={handleSearch}>
                                                검색
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 차량 리스트 */}
                        <div className="table-content">
                            <div className="table-wrapper">
                                <div className="table">
                                    <table>
                                        <colgroup>
                                            <col width={"12%"} />
                                            <col width={"17%"} />
                                            <col width={"34%"} />
                                            <col width={"17%"} />
                                            <col width={"20%"} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">선택</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">차량번호</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">차량모델</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">담당자</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">연락처</div>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {carsData && carsData.data && carsData.data.length > 0 ? (
                                                carsData.data.map((vehicle) => (
                                                    <tr key={vehicle.id}>
                                                        <td>
                                                            <div className="cell" style={{ height: "100%" }}>
                                                                <div className="input-checkbox">
                                                                    <div className="checkbox" style={{ display: "flex" }}>
                                                                        <input
                                                                            type="radio"
                                                                            name="selectedVehicle"
                                                                            id={`selectedVehicle-${vehicle.id}`}
                                                                            checked={selectedVehicle?.id === vehicle.id}
                                                                            onChange={() => handleSelectVehicle(vehicle)}
                                                                        />
                                                                        <label htmlFor={`selectedVehicle-${vehicle.id}`}></label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{vehicle.carNumber}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{vehicle.modelName}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{vehicle.color}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{vehicle.phone}</div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5">차량 데이터가 없습니다.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button type="button" className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button type="button" className="btn btn-primary btn-confirm" onClick={handleConfirm}>
                        완료
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

// 차량 반납 등록 팝업
const VehicleReturnPopup = ({ layer = true, onClose, formData, setFormData }) => {
    const { vehicleReturnContractIdx } = formData;
    const [returnData, setReturnData] = useState({
        addressPopupStatus: false,
        returnedAt: "",
        returnMethod: "",
        returnAddress: "",
        returnAddressDetail: "",
        releaseMileage: "",
        releaseGasAmount: null,
        returnMileage: "",
        returnGasAmount: null,
    });

    const [returnContract, { isLoading: isReturnLoading }] = useReturnContractMutation();

    const returnMethodOptions = [
        { value: "직접진행", label: "직접진행" },
        { value: "탁송진행", label: "탁송진행" },
        { value: "고객내방", label: "고객내방" },
    ];

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    // 반납 등록 버튼 이벤트 핸들러
    const handleReturnSubmit = async () => {
        const payload = {
            ...returnData,
            releaseMileage: Number(uncomma(returnData.releaseMileage)),
            returnMileage: Number(uncomma(returnData.returnMileage)),
        };

        try {
            await returnContract({
                contractGroupId: formData.id,
                contractId: formData.vehicleReturnContractIdx,
                body: payload,
            }).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "반납 등록이 완료되었습니다.",
            }));
            onClose();
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "반납 등록에 실패하였습니다.",
            }));
        }
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup vehicle-return-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">반납 등록</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="table-container">
                        <div className="table-filter">
                            <div className="filter-row">
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납일시</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <DatePicker
                                                locale={ko}
                                                showYearDropdown
                                                scrollableYearDropdown
                                                yearDropdownItemNumber={20}
                                                selected={returnData?.returnedAt}
                                                onChange={(date) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnedAt: date,
                                                    }));
                                                }}
                                                showTimeSelect
                                                timeIntervals={30}
                                                timeCaption="시간"
                                                dateFormat="yyyy.MM.dd HH:mm"
                                                onKeyDown={(e) => e.preventDefault()}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납방법</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <Select
                                                options={returnMethodOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="반납방법"
                                                value={fuelRateOptions.find((opt) => opt.label === returnData?.returnMethod)}
                                                onChange={(option) =>
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnMethod: option.value,
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납 주소</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="주소"
                                                name="returnAddress"
                                                value={returnData.returnAddress || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnAddress: e.target.value,
                                                    }));
                                                }}
                                                onClick={() =>
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        addressPopupStatus: true,
                                                    }))
                                                }
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head"></div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="상세주소"
                                                name="returnAddressDetail"
                                                value={returnData.returnAddressDetail || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnAddressDetail: e.target.value,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납 주행거리</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder=""
                                                name="returnMileage"
                                                value={returnData.returnMileage || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnMileage: e.target.value,
                                                    }));
                                                }}
                                                onInput={formatNumberWithCommas}
                                            />
                                            <span className="input-esset">km</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납 유류량</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <Select
                                                options={fuelRateOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="유류량 선택"
                                                value={fuelRateOptions.find((opt) => opt.label === returnData?.returnGasAmount)}
                                                onChange={(option) =>
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnGasAmount: option.value,
                                                    }))
                                                }
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">배차 주행거리</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder=""
                                                name="releaseMileage"
                                                value={returnData.releaseMileage || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        releaseMileage: e.target.value,
                                                    }));
                                                }}
                                                onInput={formatNumberWithCommas}
                                            />
                                            <span className="input-esset">km</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">배차 유류량</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <Select
                                                options={fuelRateOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="유류량 선택"
                                                value={fuelRateOptions.find((opt) => opt.label === returnData?.releaseGasAmount)}
                                                onChange={(option) =>
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        releaseGasAmount: option.value,
                                                    }))
                                                }
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button className="btn btn-primary btn-confirm" onClick={handleReturnSubmit}>
                        반납 등록
                    </button>
                </div>
            </div>

            {/* 주소 검색 팝업 */}
            {returnData.addressPopupStatus && (
                <SearchAddress
                    modalStatus={returnData.addressPopupStatus}
                    setModalStatus={() =>
                        setReturnData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setReturnData((prev) => ({
                            ...prev,
                            returnAddress: data.address,
                            returnBCode: data.bcode,
                        }))
                    }
                />
            )}
        </>,
        document.body
    );
};

// 청구기준 설정 팝업
const BillingSettingPopup = ({ layer = true, onClose, formData, setFormData, selectedContractIndex }) => {
    const { rentCorpId } = formData;
    const [activeTab, setActiveTab] = useState("billing"); // "billing" / "price"
    const [selectedPriceTableId, setSelectedPriceTableId] = useState(null);
    const [priceTableDetails, setPriceTableDetails] = useState([]); // 요금표 상세 데이터
    const [groupedData, setGroupedData] = useState({}); // 그룹화된 데이터: { [groupKey]: [...rows] }
    const [activeGroup, setActiveGroup] = useState(""); // 현재 활성화된 그룹
    const [selectedPriceTableItem, setSelectedPriceTableItem] = useState(null);

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    // Lazy hook: getRentPriceTableItems API 호출
    const [triggerPriceTableItems] = useLazyGetRentPriceTableItemsQuery();

    // formData.totalOrderCharge.chargeMethod에 따라 API 호출 처리
    useEffect(() => {
        const chargeMethod = formData.totalOrderCharge?.chargeMethod;
        // "INPERSON"이면 activeTab은 "price"로 설정하고 API는 호출하지 않음
        if (chargeMethod === "INPERSON") {
            setActiveTab("price");
            setPriceTableDetails([]);
            setGroupedData({});
            setActiveGroup("");
            setSelectedPriceTableItem(null);
            return;
        }
        // "INSURANCE_BASE"이면 insurance_corp_id를 파라미터로 전달
        if (chargeMethod === "INSURANCE_BASE") {
            const insuranceCorpId = formData.contractInsurance?.insuranceCorpId;
            if (insuranceCorpId) {
                triggerPriceTableItems({ insurance_corp_id: insuranceCorpId })
                    .unwrap()
                    .then((res) => {
                        if (res.data) {
                            setPriceTableDetails(res.data);
                            groupData(res.data);
                        }
                    })
                    .catch((err) => {
                        console.error("요금표 조회 실패:", err);
                    });
            }
        } else if (typeof chargeMethod === "number") {
            // Number 타입이면 rent_price_table_id를 파라미터로 전달
            triggerPriceTableItems({ rent_price_table_id: chargeMethod })
                .unwrap()
                .then((res) => {
                    if (res.data) {
                        setPriceTableDetails(res.data);
                        groupData(res.data);
                    }
                })
                .catch((err) => {
                    console.error("요금표 조회 실패:", err);
                });
        }
    }, [formData.totalOrderCharge?.chargeMethod, formData.contractInsurance?.insuranceCorpId, triggerPriceTableItems]);

    // 그룹화 함수: carGubun의 공백 제거 후 그룹화
    const groupData = (details) => {
        const groups = {};
        details.forEach((item) => {
            const groupKey = item.carGubun.replace(/\s+/g, "");
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(item);
        });
        setGroupedData(groups);
        const keys = Object.keys(groups);
        if (keys.length > 0) {
            setActiveGroup(keys[0]);
        } else {
            setActiveGroup("");
        }
    };

    // Select의 onChange 핸들러
    const handlePriceTableSelect = (option) => {
        setSelectedPriceTableId(option.value);
        // 초기화
        setGroupedData({});
        setActiveGroup("");
        setPriceTableDetails([]);
        setSelectedPriceTableItem(null);
    };

    const handleRegister = () => {
        // 선택된 요금표 항목이 없으면 토스트 메시지 출력
        if (!selectedPriceTableItem) {
            setFormData((prev) => ({ ...prev, toastMsg: "요금표 항목을 선택해주세요." }));
            return;
        }
        // 해당 계약(contract) 내 billingInfo 객체에 rentPrice 항목을 추가
        setFormData((prev) => ({
            ...prev,
            contracts: prev.contracts?.map((contract) =>
                contract?.id === selectedContractIndex
                    ? {
                          ...contract,
                          billingInfo: {
                              ...(contract?.billingInfo || {}),
                              rentPrice: selectedPriceTableItem,
                          },
                      }
                    : contract
            ),
        }));
        onClose();
    };

    // 그룹별 토글 버튼 렌더링
    const renderToggleButtons = () => {
        return Object.keys(groupedData).map((groupKey) => (
            <button
                key={groupKey}
                type="button"
                className={`btn-toggle ${activeGroup === groupKey ? "active" : ""}`}
                onClick={() => setActiveGroup(groupKey)}
            >
                {groupKey}
            </button>
        ));
    };

    // 활성 그룹에 해당하는 데이터를 테이블 행으로 렌더링
    const renderTableRows = () => {
        if (!activeGroup || !groupedData[activeGroup]) return null;
        return groupedData[activeGroup].map((item) => {
            const price12 = item.rentPriceItemDetails.find((d) => d.title.replace(/\s+/g, "") === "1~2일")?.price;
            const price34 = item.rentPriceItemDetails.find((d) => d.title.replace(/\s+/g, "") === "3~4일")?.price;
            const price56 = item.rentPriceItemDetails.find((d) => d.title.replace(/\s+/g, "") === "5~6일")?.price;
            const price7up = item.rentPriceItemDetails.find((d) => d.title.replace(/\s+/g, "") === "7일이상")?.price;
            return (
                <tr key={item.id}>
                    <td>
                        <div className="cell" data-align="center">
                            <div className="input-checkbox">
                                <div className="radio">
                                    <input
                                        type="radio"
                                        id={`price-${item.id}`}
                                        name="priceTableSelect"
                                        onChange={() => setSelectedPriceTableItem(item)}
                                        checked={selectedPriceTableItem && selectedPriceTableItem.id === item.id}
                                    />
                                    <label htmlFor={`price-${item.id}`} style={{ height: "24px" }}></label>
                                </div>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{item.carGubun.replace(/\s+/g, "")}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{item.modelTitle}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price12 !== undefined ? comma(price12) : "-"}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price34 !== undefined ? comma(price34) : "-"}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price56 !== undefined ? comma(price56) : "-"}</span>
                        </div>
                    </td>
                    <td>
                        <div className="cell" data-align="center">
                            <span className="cell-desc">{price7up !== undefined ? comma(price7up) : "-"}</span>
                        </div>
                    </td>
                </tr>
            );
        });
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup service-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">청구기준 설정</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    {/* <div className="tab-list">
                        <button
                            type="button"
                            className={`btn-toggle ${activeTab === "billing" ? "active" : ""}`}
                            onClick={() => setActiveTab("billing")}
                        >
                            청구기준
                        </button>
                        <button
                            type="button"
                            className={`btn-toggle ${activeTab === "price" ? "active" : ""}`}
                            onClick={() => setActiveTab("price")}
                        >
                            기준가 설정
                        </button>
                    </div> */}

                    {activeTab === "billing" && (
                        <>
                            <div className="table-container" style={{ marginTop: "0" }}>
                                {/* <div className="table-filter">
                                    <div className="filter-row">
                                        <div className="filter-item">
                                            <div className="item-head">
                                                <span className="head-tit">요금표 선택</span>
                                            </div>
                                            <div className="item-content">
                                                <div className="item-input">
                                                    <Select
                                                        options={rentPriceTableOptions}
                                                        className="custom-select"
                                                        classNamePrefix="custom-select"
                                                        isSearchable={false}
                                                        placeholder="요금표 선택"
                                                        value={rentPriceTableOptions.find((opt) => opt.value === selectedPriceTableId)}
                                                        onChange={handlePriceTableSelect}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}

                                {priceTableDetails && priceTableDetails.length > 0 && (
                                    <div className="table-content">
                                        <div className="table-option">
                                            <div className="table-caption">
                                                <div className="toggle-list">{renderToggleButtons()}</div>
                                            </div>
                                        </div>

                                        <div className="table-wrapper">
                                            <div className="table">
                                                <table>
                                                    <colgroup>
                                                        <col width="10%" />
                                                        <col width="14%" />
                                                        <col width="20%" />
                                                        <col width="14%" />
                                                        <col width="14%" />
                                                        <col width="14%" />
                                                        <col width="14%" />
                                                    </colgroup>
                                                    <thead>
                                                        <tr>
                                                            <th>
                                                                <div className="cell">
                                                                    <div className="cell-tit">선택</div>
                                                                </div>
                                                            </th>
                                                            <th>
                                                                <div className="cell">
                                                                    <div className="cell-tit">차종</div>
                                                                </div>
                                                            </th>
                                                            <th>
                                                                <div className="cell">
                                                                    <div className="cell-tit">대표모델</div>
                                                                </div>
                                                            </th>
                                                            <th>
                                                                <div className="cell">
                                                                    <div className="cell-tit">1일~2일</div>
                                                                </div>
                                                            </th>
                                                            <th>
                                                                <div className="cell">
                                                                    <div className="cell-tit">3일~4일</div>
                                                                </div>
                                                            </th>
                                                            <th>
                                                                <div className="cell">
                                                                    <div className="cell-tit">5일~6일</div>
                                                                </div>
                                                            </th>
                                                            <th>
                                                                <div className="cell">
                                                                    <div className="cell-tit">7일이상</div>
                                                                </div>
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>{renderTableRows()}</tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === "price" && (
                        <>
                            <div className="content-textbox">
                                <div className="item-list">
                                    <div className="item">
                                        <span className="item-desc">업체의 요금 기준가로 변경 합니다.</span>
                                    </div>
                                    <div className="item">
                                        <span className="item-input">
                                            <input type="text" placeholder="기준가를 입력해주세요." />
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="content-description">
                                <p>
                                    <em>청구 기준가를 업체의 기준으로 변경 시 사업자의 요금표 파일을 첨부하여 청구 해주셔야 합니다.</em>
                                </p>
                                <p>요금표는 사업자 정보 관리에서 파일을 업로드 해주세요.</p>
                            </div>
                        </>
                    )}
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button className="btn btn-primary btn-confirm" onClick={handleRegister}>
                        완료
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};
